import React, { useState, useMemo, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';

// ==========================================================
// gameProducts.js
// Konfigurasi game + mapping produk (kode -> jumlah Diamond)
// Free Fire: dibuat dari file daftar-produk-prabayar (72 produk).
// Ganti / sambungkan dengan data produk dari database Anda.
// ==========================================================

const GAME_ORDER = ['FF', 'ML'];

const GAME_CONFIG = {
  FF: {
    id: 'FF',
    label: 'Free Fire',
    title: 'TOP UP FREE FIRE',
    unit: 'Diamond',
    // FFP = produk reguler, PFF = produk promo
    codePattern: /^(FFP|PFF)[A-Z0-9]+$/,
    defaultMarkup: 2.5,
    // true = harga jual diambil dari HPP GamesPanel (FFP_DATA x rate)
    hppFromGamesPanel: true,
    // true  = kode WAJIB ada di mapping produk, kalau tidak -> "tidak ditemukan"
    strictMapping: true,
    fallbackDiamond: null,
  },
  ML: {
    id: 'ML',
    label: 'Mobile Legends',
    title: 'TOP UP MOBILE LEGENDS',
    unit: 'Diamonds',
    // SESUAIKAN dengan format kode Mobile Legends di sistem Anda
    codePattern: /^ML[A-Z0-9]*$/,
    defaultMarkup: 0,
    hppFromGamesPanel: false,
    // Selama ML_MAPPING belum diisi, kode ML dikenali dari angka pada kode
    // (mis. ML86 -> 86) dan ditandai "Mapping otomatis".
    // Setelah mapping ML lengkap, ubah strictMapping menjadi true.
    strictMapping: false,
    fallbackDiamond: /^ML(\d+)$/,
  },
};

// ----------------------------------------------------------
// MAPPING FREE FIRE: [kode, jumlah Diamond, (false = tidak aktif)]
// ----------------------------------------------------------
const FF_MAPPING = [
  ['FFPCEK', 0, false],
  ['FFP5', 5],
  ['FFP10', 10],
  ['FFP12', 12],
  ['PFF12', 12],
  ['FFP20', 20],
  ['FFP25', 25],
  ['FFP30', 30],
  ['FFP40', 40],
  ['FFP50', 50],
  ['PFF50', 50],
  ['FFP70', 70],
  ['PFF70', 70],
  ['FFP75', 75],
  ['FFP80', 80],
  ['FFP90', 90],
  ['FFP95', 95],
  ['FFP100', 100],
  ['FFP120', 120],
  ['FFP130', 130],
  ['FFP140', 140],
  ['PFF140', 140],
  ['FFP145', 145],
  ['FFP150', 150],
  ['FFP160', 160],
  ['FFP190', 190],
  ['FFP200', 200],
  ['FFP210', 210],
  ['FFP250', 250],
  ['FFP280', 280],
  ['FFP350', 350],
  ['FFP355', 355],
  ['PFF355', 355],
  ['FFP375', 375],
  ['FFP400', 400],
  ['FFP405', 405],
  ['FFP425', 425],
  ['FFP475', 475],
  ['FFP500', 500],
  ['FFP510', 510],
  ['FFP545', 545],
  ['FFP565', 565],
  ['FFP600', 600],
  ['FFP635', 635],
  ['FFP700', 700],
  ['FFP720', 720],
  ['PFF720', 720],
  ['FFP770', 770],
  ['FFP790', 790],
  ['FFP800', 800],
  ['FFP860', 860],
  ['FFP930', 930],
  ['FFP1000', 1000],
  ['FFP1050', 1050],
  ['FFP1075', 1075],
  ['FFP1080', 1080],
  ['FFP1200', 1200],
  ['FFP1300', 1300],
  ['FFP1440', 1440],
  ['FFP1450', 1450],
  ['PFF1450', 1450],
  ['FFP2000', 2000],
  ['FFP2140', 2140],
  ['FFP2180', 2180],
  ['PFF2180', 2180],
  ['FFP2355', 2355],
  ['FFP2720', 2720],
  ['FFP3640', 3640],
  ['PFF3640', 3640],
  ['FFP4000', 4000],
  ['FFP6000', 6000],
  ['FFP7290', 7290],
];

// ----------------------------------------------------------
// MAPPING MOBILE LEGENDS: [kode, jumlah Diamond, (false = tidak aktif)]
// Isi sesuai data produk Anda, contoh format: ['ML86', 86],
// ----------------------------------------------------------
const ML_MAPPING = [];

const toProducts = (game, mapping) =>
  mapping.map(([code, diamond, active]) => ({
    code,
    game,
    diamond,
    active: active !== false,
  }));

const DEFAULT_PRODUCTS = [
  ...toProducts('FF', FF_MAPPING),
  ...toProducts('ML', ML_MAPPING),
];

// ==========================================================
// DATA & RUMUS HPP FREE FIRE
// Data dan rumus sama persis dengan GamesPanel.js (FFP_DATA, generateFFP).
// Rate disimpan di localStorage (kunci sama dengan GamesPanel versi patch),
// jadi kalau GamesPanel dipatch, rate-nya ikut sinkron.
// ==========================================================
const FFP_DATA = [
    { id: "FFP5", dm: "5 Diamond", sum: 3 },
    { id: "FFP10", dm: "10 Diamond", sum: 6 },
    { id: "FFP12", dm: "12 Diamond", sum: 6.73 },
    { id: "FFP20", dm: "20 Diamond", sum: 12 },
    { id: "FFP25", dm: "25 Diamond", sum: 15 },
    { id: "FFP30", dm: "30 Diamond", sum: 18 },
    { id: "FFP40", dm: "40 Diamond", sum: 23.96 },
    { id: "FFP50", dm: "50 Diamond", sum: 24 },
    { id: "FFP70", dm: "70 Diamond", sum: 33 },
    { id: "FFP75", dm: "75 Diamond", sum: 36 },
    { id: "FFP80", dm: "80 Diamond", sum: 39 },
    { id: "FFP90", dm: "90 Diamond", sum: 45 },
    { id: "FFP95", dm: "95 Diamond", sum: 48 },
    { id: "FFP100", dm: "100 Diamond", sum: 48 },
    { id: "FFP120", dm: "120 Diamond", sum: 57 },
    { id: "FFP130", dm: "130 Diamond", sum: 63 },
    { id: "FFP144", dm: "140 Diamond", sum: 66 },
    { id: "FFP145", dm: "145 Diamond", sum: 69 },
    { id: "FFP150", dm: "150 Diamond", sum: 72 },
    { id: "FFP160", dm: "160 Diamond", sum: 78 },
    { id: "FFP190", dm: "190 Diamond", sum: 90.05 },
    { id: "FFP200", dm: "200 Diamond", sum: 96 },
    { id: "FFP210", dm: "210 Diamond", sum: 99 },
    { id: "FFP250", dm: "250 Diamond", sum: 120 },
    { id: "FFP280", dm: "280 Diamond", sum: 132 },
    { id: "FFP350", dm: "350 Diamond", sum: 164.96 },
    { id: "FFP355", dm: "355 Diamond", sum: 165 },
    { id: "FFP375", dm: "375 Diamond", sum: 177 },
    { id: "FFP400", dm: "400 Diamond", sum: 189 },
    { id: "FFP405", dm: "405 Diamond", sum: 189.03 },
    { id: "FFP425", dm: "425 Diamond", sum: 198.06 },
    { id: "FFP475", dm: "475 Diamond", sum: 222.01 },
    { id: "FFP500", dm: "500 Diamond", sum: 234.05 },
    { id: "FFP510", dm: "510 Diamond", sum: 240 },
    { id: "FFP545", dm: "545 Diamond", sum: 255 },
    { id: "FFP565", dm: "565 Diamond", sum: 264 },
    { id: "FFP600", dm: "600 Diamond", sum: 282 },
    { id: "FFP635", dm: "635 Diamond", sum: 297 },
    { id: "FFP700", dm: "700 Diamond", sum: 330 },
    { id: "FFP720", dm: "720 Diamond", sum: 330 },
    { id: "FFP770", dm: "770 Diamond", sum: 354.2 },
    { id: "FFP790", dm: "790 Diamond", sum: 363 },
    { id: "FFP800", dm: "800 Diamond", sum: 369 },
    { id: "FFP860", dm: "860 Diamond", sum: 396.1 },
    { id: "FFP930", dm: "930 Diamond", sum: 429 },
    { id: "FFP1000", dm: "1000 Diamond", sum: 462 },
    { id: "FFP1050", dm: "1050 Diamond", sum: 486 },
    { id: "FFP1075", dm: "1075 Diamond", sum: 495 },
    { id: "FFP1080", dm: "1080 Diamond", sum: 498 },
    { id: "FFP1200", dm: "1200 Diamond", sum: 555 },
    { id: "FFP1300", dm: "1300 Diamond", sum: 603 },
    { id: "FFP1440", dm: "1440 Diamond", sum: 660 },
    { id: "FFP1450", dm: "1450 Diamond", sum: 666 },
    { id: "FFP2000", dm: "2000 Diamond", sum: 924 },
    { id: "FFP2140", dm: "2140 Diamond", sum: 990 },
    { id: "FFP2180", dm: "2180 Diamond", sum: 996 },
    { id: "FFP2355", dm: "2355 Diamond", sum: 1080 },
    { id: "FFP2720", dm: "2720 Diamond", sum: 1251 },
    { id: "FFP3640", dm: "3640 Diamond", sum: 1659 },
    { id: "FFP4000", dm: "4000 Diamond", sum: 1827 },
    { id: "FFP6000", dm: "6000 Diamond", sum: 2742 },
    { id: "FFP7290", dm: "7290 Diamond", sum: 3300 }
];

const FF_RATE_KEY = 'ffHppRateSettings';
const FF_DEFAULT_RATE = { rateTotal: 90175, rateDivider: 330 };

const loadFFRate = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(FF_RATE_KEY) || 'null');
    if (saved && Number(saved.rateTotal) > 0 && Number(saved.rateDivider) > 0) {
      return {
        rateTotal: Number(saved.rateTotal),
        rateDivider: Number(saved.rateDivider),
      };
    }
  } catch (e) {
    // abaikan, pakai default
  }
  return { ...FF_DEFAULT_RATE };
};

const saveFFRate = (rate) => {
  try {
    localStorage.setItem(FF_RATE_KEY, JSON.stringify(rate));
  } catch (e) {
    // abaikan jika storage tidak tersedia
  }
};

// Rumus HPP: ceil(unit x (modal / pembagi))
const calcFFHpp = (sum, rate) => {
  const currentRate = rate.rateTotal / (rate.rateDivider || 1);
  return Math.ceil(sum * currentRate);
};

// ==========================================================
// FITUR BARU: UPDATE HARGA GAME VIA EXCEL
// ==========================================================
const SETTINGS_KEY = 'gamePriceUpdateSettings';
const PRICES_KEY = 'gamePriceOverrides';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const CODE_HEADERS = [
  'kode produk',
  'kode',
  'sku',
  'code',
  'buyer sku code',
  'kode_produk',
];
// Urutan = prioritas. "harga supplier" sengaja paling akhir.
const PRICE_HEADERS = [
  'harga',
  'harga dasar',
  'harga excel',
  'harga jual',
  'harga baru',
  'harga baru / margin',
  'price',
  'harga supplier',
];

// Kode di Excel yang datanya HPP-nya memakai kode lain di GamesPanel
// (di GamesPanel tertulis FFP144 untuk 140 Diamond, di Excel tertulis FFP140)
const HPP_ALIAS = { FFP140: 'FFP144' };

// ----------------------------------------------------------
// KODE BUYER DIGIFLAZZ (FREFIRE...) -> KODE GAMES PANEL (FFP...)
// Excel buyer memakai kode FREFIRE<angka>, tetapi angkanya TIDAK selalu
// sama dengan jumlah diamond (FREFIRE0 = 90 DM, FREFIRE40 = 140 DM,
// FREFIRE60 = 160 DM, FREFIRE2770 = 2720 DM).
// Karena itu jumlah diamond dibaca dari kolom "Produk" (nama produk),
// lalu dijadikan kode FFP<diamond>. Kode FFP inilah yang dipakai untuk
// HPP Games Panel dan untuk teks broadcast Telegram.
// ----------------------------------------------------------
const BUYER_FF_PATTERN = /^FRE{1,2}FIRE/; // FREFIRE & FREEFIRE
const NAME_HEADERS = ['produk', 'nama produk', 'product name', 'nama'];
const STATUS_HEADERS = ['status'];

// "Free Fire 1.050 Diamond" -> 1050
const diamondFromName = (name) => {
  const m = String(name || '').match(/(\d[\d.,]*)\s*(?:diamonds?|dm|dia)\b/i);
  if (!m) return null;
  const n = Number(m[1].replace(/[.,]/g, ''));
  return isFinite(n) && n > 0 ? n : null;
};

// Hasil: { code, sourceCode, note }
//  - kode bukan FREFIRE  -> dikembalikan apa adanya (sourceCode = null)
//  - kode FREFIRE        -> diubah menjadi FFP<diamond>
const toGamesPanelCode = (rawCode, name) => {
  const code = normCode(rawCode);
  if (!BUYER_FF_PATTERN.test(code)) {
    return { code, sourceCode: null, note: '' };
  }

  const fromName = diamondFromName(name);
  const tail = code.match(/(\d+)$/);
  const fromCode = tail ? Number(tail[1]) : null;
  const diamond = fromName || fromCode;

  if (!diamond) {
    // tidak bisa diterjemahkan -> biarkan, nanti ditandai "tidak ditemukan"
    return { code, sourceCode: code, note: '' };
  }

  return {
    code: `FFP${diamond}`,
    sourceCode: code,
    note: fromName
      ? ''
      : 'Diamond diambil dari angka pada kode (nama produk tidak terbaca)',
  };
};

const ERROR_LABELS = {
  kode_kosong: 'baris tanpa kode produk',
  duplikat: 'kode produk duplikat',
  game_salah: 'kode produk bukan milik game yang dipilih',
  tidak_ditemukan: 'produk tidak ditemukan',
  nonaktif: 'produk tidak aktif',
  harga_kosong: 'harga kosong',
  harga_invalid: 'harga bukan angka / tidak valid',
};

const formatRupiah = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '-';
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const normCode = (v) =>
  String(v === null || v === undefined ? '' : v)
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');

const isBlank = (v) =>
  v === null || v === undefined || String(v).trim() === '';

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// Baca harga dari sel Excel (angka, "1.600", "1,600", "Rp 1.600", "1795,5", dll)
const parsePrice = (raw) => {
  if (isBlank(raw)) return { error: 'harga_kosong', message: 'Harga kosong' };

  if (typeof raw === 'number') {
    if (!isFinite(raw))
      return { error: 'harga_invalid', message: 'Harga bukan angka' };
    if (raw <= 0)
      return { error: 'harga_invalid', message: 'Harga harus lebih dari 0' };
    return { value: raw };
  }

  let s = String(raw)
    .trim()
    .replace(/^rp\.?\s*/i, '')
    .replace(/\s/g, '');

  if (!/^[0-9.,]+$/.test(s))
    return { error: 'harga_invalid', message: 'Harga bukan angka' };

  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) {
    // format Indonesia: 1.600 atau 1.600,50
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) {
    // format US: 1,600 atau 1,600.50
    s = s.replace(/,/g, '');
  } else if (/^\d+,\d+$/.test(s)) {
    // desimal koma: 1795,5
    s = s.replace(',', '.');
  }

  const n = Number(s);
  if (isNaN(n) || !isFinite(n))
    return { error: 'harga_invalid', message: 'Harga bukan angka' };
  if (n <= 0)
    return { error: 'harga_invalid', message: 'Harga harus lebih dari 0' };
  return { value: n };
};

// Hitung harga jual: harga dasar + markup%, lalu dibulatkan ke kelipatan "step"
// Memakai aritmetika basis-poin agar tidak ada selisih floating point.
// Contoh: 1795 x 2,5% = 1839,875 -> 1.840
const calcSell = (base, markupPct, step) => {
  const bp = Math.round(markupPct * 100);
  const raw = (base * (10000 + bp)) / 10000;
  const s = step > 0 ? step : 1;
  return Math.round(raw / s + 1e-9) * s;
};

// Urutan: harga jual termurah -> termahal.
// Jika harga sama, urut berdasarkan jumlah Diamond, lalu kode produk.
// Baris tanpa harga jual (error) selalu di paling bawah.
const compareByPrice = (a, b) => {
  const pa = a.sell === null || a.sell === undefined ? Infinity : a.sell;
  const pb = b.sell === null || b.sell === undefined ? Infinity : b.sell;
  if (pa !== pb) return pa < pb ? -1 : 1;
  return (
    (a.diamond ?? Infinity) - (b.diamond ?? Infinity) ||
    String(a.code).localeCompare(String(b.code), 'en', { numeric: true })
  );
};

// Susun teks broadcast Telegram (monospace, rata kanan)
const buildTelegramText = (game, validRows, sortByPrice, codeBlock) => {
  if (!validRows.length) return '';

  const rows = [...validRows];
  if (sortByPrice) rows.sort(compareByPrice);

  const codeW = Math.max(...rows.map((r) => r.code.length));
  const diaW = Math.max(...rows.map((r) => String(r.diamond).length));
  const priceW = Math.max(...rows.map((r) => formatRupiah(r.sell).length));

  const lines = rows.map(
    (r) =>
      `${r.code.padEnd(codeW, ' ')}  ${String(r.diamond).padStart(
        diaW,
        ' '
      )} ${game.unit}  ${formatRupiah(r.sell).padStart(priceW, ' ')}`
  );

  const width = Math.max(game.title.length, ...lines.map((l) => l.length));
  const text = `${game.title}\n${'='.repeat(width)}\n\n${lines.join('\n')}`;

  return codeBlock ? '```\n' + text + '\n```' : text;
};

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // lanjut ke fallback
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
};

// Baca file Excel/CSV -> daftar baris { line, code, price }
const readSheetFile = (file, onProgress) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 60));
    };
    reader.onerror = () => reject(new Error('File tidak dapat dibaca.'));
    reader.onload = (e) => {
      try {
        onProgress(75);
        const wb = XLSX.read(new Uint8Array(e.target.result), {
          type: 'array',
        });
        if (!wb.SheetNames.length) {
          reject(new Error('Format Excel salah: file tidak memiliki sheet.'));
          return;
        }
        const ws = wb.Sheets[wb.SheetNames[0]];
        const matrix = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          defval: '',
          raw: true,
        });

        const nonEmpty = matrix.filter((r) =>
          r.some((c) => String(c).trim() !== '')
        );
        if (!nonEmpty.length) {
          reject(new Error('File Excel kosong. Tidak ada data yang dibaca.'));
          return;
        }

        // Deteksi kolom lewat header; jika tidak ada pakai kolom 1 (kode) & 2 (harga)
        const first = matrix[0] || [];
        const norm = first.map((c) => String(c).trim().toLowerCase());
        let codeIdx = norm.findIndex((h) => CODE_HEADERS.includes(h));
        let priceIdx = PRICE_HEADERS.map((h) => norm.indexOf(h)).find(
          (i) => i >= 0
        );
        if (priceIdx === undefined) priceIdx = -1;
        // Kolom opsional (format Digiflazz buyer): Produk & Status
        const nameIdx = norm.findIndex((h) => NAME_HEADERS.includes(h));
        const statusIdx = norm.findIndex((h) => STATUS_HEADERS.includes(h));
        let start = 0;

        if (codeIdx >= 0 && priceIdx >= 0) {
          start = 1;
        } else {
          codeIdx = 0;
          priceIdx = 1;
          const firstCode = normCode(first[0]);
          const looksLikeCode = GAME_ORDER.some((id) =>
            GAME_CONFIG[id].codePattern.test(firstCode)
          );
          if (
            !looksLikeCode &&
            !isBlank(first[1]) &&
            parsePrice(first[1]).error
          ) {
            start = 1; // baris pertama dianggap judul kolom
          }
        }

        const rows = [];
        for (let i = start; i < matrix.length; i++) {
          const r = matrix[i];
          rows.push({
            line: i + 1,
            code: r[codeIdx],
            price: r[priceIdx],
            name: nameIdx >= 0 ? r[nameIdx] : '',
            status: statusIdx >= 0 ? r[statusIdx] : '',
          });
        }

        const hasAny = rows.some((r) => !isBlank(r.code) || !isBlank(r.price));
        if (!hasAny) {
          reject(
            new Error(
              'Format Excel salah: tidak ada data. Gunakan kolom "Kode Produk" dan "Harga".'
            )
          );
          return;
        }

        const maxCols = Math.max(...nonEmpty.map((r) => r.length));
        if (maxCols < 2) {
          reject(
            new Error(
              'Format Excel salah: dibutuhkan 2 kolom, yaitu "Kode Produk" dan "Harga".'
            )
          );
          return;
        }

        onProgress(90);
        resolve(rows);
      } catch (err) {
        reject(new Error('Format Excel salah: file tidak dapat diproses.'));
      }
    };

    reader.readAsArrayBuffer(file);
  });

// Default penerapan harga bila prop onApply tidak diberikan:
// disimpan ke localStorage. Sambungkan ke API/database Anda lewat prop onApply.
const defaultApply = async (payload) => {
  let current = {};
  try {
    current = JSON.parse(localStorage.getItem(PRICES_KEY) || '{}');
  } catch (e) {
    current = {};
  }
  const now = new Date().toISOString();
  payload.items.forEach((item) => {
    current[item.code] = {
      game: payload.game,
      diamond: item.diamond,
      supplierPrice: item.supplierPrice,
      sellPrice: item.sellPrice,
      markupPercent: payload.markupPercent,
      updatedAt: now,
    };
  });
  localStorage.setItem(PRICES_KEY, JSON.stringify(current));
};

const loadSettings = () => {
  const base = {
    markups: {},
    roundStep: 1,
    codeBlock: true,
    sort: true,
    ffSource: 'hpp',
  };
  GAME_ORDER.forEach((id) => {
    base.markups[id] = String(GAME_CONFIG[id].defaultMarkup).replace('.', ',');
  });
  try {
    const saved = JSON.parse(localStorage.getItem(SETTINGS_KEY) || 'null');
    if (saved && typeof saved === 'object') {
      return {
        markups: { ...base.markups, ...(saved.markups || {}) },
        roundStep: saved.roundStep || base.roundStep,
        codeBlock:
          typeof saved.codeBlock === 'boolean'
            ? saved.codeBlock
            : base.codeBlock,
        sort: typeof saved.sort === 'boolean' ? saved.sort : base.sort,
        ffSource: saved.ffSource === 'markup' ? 'markup' : 'hpp',
      };
    }
  } catch (e) {
    // abaikan
  }
  return base;
};

// ICON KECIL
const Icon = ({ d, className = 'w-4 h-4', sw = 2 }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={sw}
      d={d}
    />
  </svg>
);

const ICONS = {
  upload: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12',
  table:
    'M3 10h18M3 14h18M10 3v18M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z',
  send: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8',
  copy: 'M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3',
  check: 'M5 13l4 4L19 7',
  x: 'M6 18L18 6M6 6l12 12',
  alert:
    'M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
  download: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4',
};

// KOMPONEN UTAMA UPDATE HARGA GAME
const GamePriceUpdate = ({ products = DEFAULT_PRODUCTS, onApply }) => {
  const initial = useMemo(loadSettings, []);

  const [gameId, setGameId] = useState('FF');
  const [markups, setMarkups] = useState(initial.markups);
  const [roundStep, setRoundStep] = useState(initial.roundStep);
  const [codeBlock, setCodeBlock] = useState(initial.codeBlock);
  const [sortByDiamond, setSortByDiamond] = useState(initial.sort);
  const [ffSource, setFfSource] = useState(initial.ffSource); // 'hpp' | 'markup'
  const [rate, setRate] = useState(loadFFRate);

  const [sheet, setSheet] = useState(null); // { fileName, rows }
  const [reading, setReading] = useState({ active: false, progress: 0 });
  const [fileError, setFileError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const [view, setView] = useState('table'); // 'table' | 'telegram'
  const [filter, setFilter] = useState('all'); // 'all' | 'ok' | 'error'
  const [copied, setCopied] = useState(false);

  const [ignoreErrors, setIgnoreErrors] = useState(false);
  const [applyState, setApplyState] = useState('idle'); // idle|confirm|applying|done|failed
  const [applyError, setApplyError] = useState('');

  const fileInputRef = useRef(null);
  const previewRef = useRef(null);

  const game = GAME_CONFIG[gameId];

  // Simpan pengaturan
  useEffect(() => {
    try {
      localStorage.setItem(
        SETTINGS_KEY,
        JSON.stringify({
          markups,
          roundStep,
          codeBlock,
          sort: sortByDiamond,
          ffSource,
        })
      );
    } catch (e) {
      // abaikan jika storage tidak tersedia
    }
  }, [markups, roundStep, codeBlock, sortByDiamond, ffSource]);

  // Matikan toast salin otomatis
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2200);
    return () => clearTimeout(t);
  }, [copied]);

  // ----- HPP Free Fire dari GamesPanel -----
  useEffect(() => {
    const refresh = () => setRate(loadFFRate());
    window.addEventListener('storage', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  useEffect(() => {
    if (rate.rateTotal > 0 && rate.rateDivider > 0) saveFFRate(rate);
  }, [rate]);

  const hppMap = useMemo(() => {
    const m = {};
    FFP_DATA.forEach((it) => {
      m[normCode(it.id)] = it;
    });
    Object.keys(HPP_ALIAS).forEach((k) => {
      if (m[HPP_ALIAS[k]]) m[k] = m[HPP_ALIAS[k]];
    });
    return m;
  }, []);

  const useHpp = !!game.hppFromGamesPanel && ffSource === 'hpp';
  const ratePerUnit = rate.rateTotal / (rate.rateDivider || 1);

  // ----- Markup -----
  const markupRaw = markups[gameId] ?? '0';
  const markupNum = Number(String(markupRaw).trim().replace(',', '.'));
  const markupValid =
    String(markupRaw).trim() !== '' &&
    isFinite(markupNum) &&
    markupNum >= 0 &&
    markupNum <= 100;
  const markupPct = markupValid ? markupNum : null;

  // ----- Peta produk -----
  const productMap = useMemo(() => {
    const m = {};
    products.forEach((p) => {
      m[normCode(p.code)] = p;
    });
    return m;
  }, [products]);

  // ----- Validasi + perhitungan -----
  const analysis = useMemo(() => {
    // Free Fire mode HPP: daftar produk tampil langsung dari HPP GamesPanel,
    // Excel hanya opsional (pembanding harga). Mode lain tetap butuh Excel.
    if (!sheet && !useHpp) return null;

    // Terjemahkan kode buyer (FREFIRE...) -> kode Games Panel (FFP...)
    const prepared = (sheet ? sheet.rows : []).map((r) => {
      const t = toGamesPanelCode(r.code, r.name);
      return { ...r, code: t.code, sourceCode: t.sourceCode, srcNote: t.note };
    });

    // Produk ber-HPP yang tidak ada di Excel (atau semuanya, bila tanpa Excel)
    if (useHpp) {
      const have = new Set(prepared.map((r) => normCode(r.code)));
      products.forEach((p) => {
        const c = normCode(p.code);
        if (p.game === gameId && p.active !== false && hppMap[c] && !have.has(c)) {
          prepared.push({
            line: 0,
            code: c,
            price: '',
            name: '',
            status: '',
            sourceCode: null,
            srcNote: '',
          });
        }
      });
    }

    const counts = {};
    prepared.forEach((r) => {
      const c = normCode(r.code);
      if (c) counts[c] = (counts[c] || 0) + 1;
    });

    const rows = [];

    prepared.forEach((r) => {
      const code = normCode(r.code);
      if (!code && isBlank(r.price)) return; // baris kosong dilewati

      const row = {
        line: r.line,
        code,
        sourceCode: r.sourceCode, // kode asli di Excel (FREFIRE...) bila diterjemahkan
        base: null,
        diamond: null,
        sell: null,
        diff: null, // harga jual dikurangi harga Excel (pembanding)
        status: 'ok',
        type: null,
        message: '',
      };

      const fail = (type, message) => {
        if (row.status !== 'error') {
          row.status = 'error';
          row.type = type;
          row.message = message;
        }
      };

      const warn = (message) => {
        if (row.status !== 'error') {
          row.status = 'warn';
          row.message = row.message ? `${row.message}; ${message}` : message;
        }
      };

      const priceRes = parsePrice(r.price);
      if (!priceRes.error) row.base = priceRes.value;

      const product = code ? productMap[code] : null;

      if (!code) {
        fail('kode_kosong', 'Kode produk kosong');
      } else if (counts[code] > 1) {
        fail('duplikat', `Kode muncul ${counts[code]}x di Excel`);
      } else if (product && product.game !== gameId) {
        fail(
          'game_salah',
          `Kode milik ${GAME_CONFIG[product.game]?.label || product.game}`
        );
      } else if (!product) {
        const otherGame = GAME_ORDER.filter((id) => id !== gameId).some((id) =>
          GAME_CONFIG[id].codePattern.test(code)
        );
        if (otherGame) {
          fail('game_salah', 'Kode milik game lain');
        } else if (
          !game.strictMapping &&
          game.codePattern.test(code) &&
          game.fallbackDiamond &&
          game.fallbackDiamond.test(code)
        ) {
          row.diamond = Number(code.match(game.fallbackDiamond)[1]);
          row.status = 'warn';
          row.message = 'Mapping otomatis dari kode';
        } else {
          fail('tidak_ditemukan', 'Kode produk tidak ditemukan');
        }
      } else if (product.active === false) {
        fail('nonaktif', 'Produk tidak aktif');
      } else if (
        !isBlank(r.status) &&
        String(r.status).trim().toLowerCase() !== 'aktif'
      ) {
        fail('nonaktif', `Status di Excel: ${String(r.status).trim()}`);
      } else {
        row.diamond = product.diamond;
      }

      if (r.srcNote) warn(r.srcNote);

      const hppItem = useHpp && code ? hppMap[code] : null;

      // Mode HPP: harga Excel yang kosong tidak dianggap error (harga jual dari HPP)
      if (priceRes.error && !(useHpp && priceRes.error === 'harga_kosong')) {
        fail(priceRes.error, priceRes.message);
      }

      if (row.status !== 'error') {
        if (hppItem) {
          row.sell = calcFFHpp(hppItem.sum, rate);
          row.hpp = true;
        } else if (row.base !== null && markupPct !== null) {
          row.sell = calcSell(row.base, markupPct, roundStep);
          if (useHpp) warn('Pakai markup (HPP tidak ada di GamesPanel)');
        }

        if (row.sell === null) {
          fail('harga_invalid', 'Harga jual tidak dapat dihitung');
        } else if (row.base !== null) {
          row.diff = row.sell - row.base;
          if (row.sell < row.base) {
            warn(
              useHpp
                ? 'Harga jual di bawah harga Excel'
                : 'Harga jual di bawah harga dasar'
            );
          }
        }
      }

      rows.push(row);
    });

    const validRows = rows.filter((r) => r.status !== 'error');
    const errorRows = rows.filter((r) => r.status === 'error');

    const groups = {};
    errorRows.forEach((r) => {
      if (!groups[r.type]) groups[r.type] = [];
      const label = r.code || `Baris ${r.line}`;
      if (!groups[r.type].includes(label)) groups[r.type].push(label);
    });

    return { rows, validRows, errorRows, groups };
  }, [sheet, gameId, game, products, productMap, markupPct, roundStep, useHpp, hppMap, rate]);

  const validRows = analysis ? analysis.validRows : [];
  const errorCount = analysis ? analysis.errorRows.length : 0;

  const telegramText = useMemo(
    () =>
      markupPct === null && !useHpp
        ? ''
        : buildTelegramText(game, validRows, sortByDiamond, codeBlock),
    [game, validRows, sortByDiamond, codeBlock, markupPct, useHpp]
  );

  const maxLineWidth = useMemo(() => {
    if (!telegramText) return 0;
    return Math.max(
      ...telegramText
        .split('\n')
        .filter((l) => l !== '```')
        .map((l) => l.length)
    );
  }, [telegramText]);

  // Tabel preview ikut diurutkan dari harga termurah (baris error di bawah)
  const visibleRows = useMemo(() => {
    if (!analysis) return [];
    let list = analysis.rows;
    if (filter === 'ok') list = analysis.validRows;
    else if (filter === 'error') list = analysis.errorRows;
    return sortByDiamond ? [...list].sort(compareByPrice) : list;
  }, [analysis, filter, sortByDiamond]);

  const canApply =
    !!analysis &&
    validRows.length > 0 &&
    (useHpp || markupPct !== null) &&
    (errorCount === 0 || ignoreErrors) &&
    applyState === 'idle';

  // Posisi langkah pada indikator alur
  const currentStep = !analysis
    ? 0
    : applyState === 'done'
    ? 3
    : view === 'telegram'
    ? 2
    : 1;

  // ----- Handler -----
  const handleFile = async (file) => {
    if (!file) return;
    setFileError('');
    setApplyState('idle');
    setApplyError('');
    setIgnoreErrors(false);

    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      setFileError(
        'Format file salah. Gunakan file Excel (.xlsx / .xls) atau .csv.'
      );
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setFileError('Ukuran file terlalu besar (maksimal 5 MB).');
      return;
    }

    setReading({ active: true, progress: 5 });
    try {
      const rows = await readSheetFile(file, (p) =>
        setReading({ active: true, progress: p })
      );
      setReading({ active: true, progress: 100 });
      await delay(350);
      setSheet({ fileName: file.name, rows });
      setView('table');
      setFilter('all');
    } catch (err) {
      setSheet(null);
      setFileError(err.message || 'File tidak dapat diproses.');
    } finally {
      setReading({ active: false, progress: 0 });
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    handleFile(file);
  };

  const scrollToPreview = () => {
    if (previewRef.current) {
      previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handlePreview = () => {
    setView('table');
    scrollToPreview();
  };

  const handlePreviewTelegram = () => {
    setView('telegram');
    scrollToPreview();
  };

  const handleCopy = async () => {
    if (!telegramText) return;
    const ok = await copyToClipboard(telegramText);
    if (ok) setCopied(true);
  };

  const handleCancel = () => {
    setSheet(null);
    setFileError('');
    setView('table');
    setFilter('all');
    setApplyState('idle');
    setApplyError('');
    setIgnoreErrors(false);
    setCopied(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const runApply = async () => {
    if (!analysis || (markupPct === null && !useHpp)) return;
    setApplyState('applying');
    setApplyError('');

    const payload = {
      game: gameId,
      priceSource: useHpp ? 'hpp-gamespanel' : 'excel-markup',
      hppRate: useHpp ? { ...rate } : null,
      markupPercent: markupPct,
      roundingStep: roundStep,
      items: validRows.map((r) => ({
        code: r.code,
        diamond: r.diamond,
        supplierPrice: r.base, // harga dasar/supplier dari Excel (tidak diubah)
        sellPrice: r.sell, // harga jual (HPP GamesPanel atau hasil markup)
        hppUsed: r.hpp === true,
        supplierCode: r.sourceCode || r.code, // kode buyer Digiflazz (FREFIRE...)
      })),
    };

    try {
      const applier = onApply || defaultApply;
      await applier(payload);
      await delay(500);
      setApplyState('done');
    } catch (err) {
      setApplyError(
        err && err.message ? err.message : 'Gagal menerapkan harga.'
      );
      setApplyState('failed');
    }
  };

  const handleDownloadSample = () => {
    const samples = products.filter((p) => p.game === gameId).slice(0, 3);
    const prices = [800, 1600, 1795];
    const data = [['Kode Produk', 'Harga']];
    if (samples.length) {
      samples.forEach((p, i) => data.push([p.code, prices[i]]));
    } else {
      data.push(['KODE-PRODUK', 1000]);
    }
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [{ wch: 18 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Harga');
    XLSX.writeFile(
      wb,
      `Contoh_Excel_Update_Harga_${game.label.replace(/\s+/g, '_')}.xlsx`
    );
  };

  // ----- Render -----
  const steps = useHpp
    ? ['Atur Rate HPP', 'Preview Data', 'Preview Telegram', 'Terapkan']
    : ['Upload Excel', 'Preview Data', 'Preview Telegram', 'Terapkan'];

  // Kolom tabel preview. Mode HPP: harga Excel hanya muncul bila Excel diupload.
  const gameCols = [
    ['Kode', 'text-left'],
    ['Diamond', 'text-left'],
    ...(!useHpp || sheet
      ? [[useHpp ? 'Harga Excel' : 'Harga Dasar', 'text-right']]
      : []),
    ['Harga Jual', 'text-right'],
    ...(useHpp && sheet ? [['Selisih', 'text-right']] : []),
    ['Status', 'text-left'],
  ];

  const btnBase =
    'text-xs font-bold px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 shadow-sm active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100';

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6 font-sans relative text-slate-800">
      <style>{`
        @keyframes gpu-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes gpu-pop { 0% { transform: scale(.6); opacity: 0; } 70% { transform: scale(1.08); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
        @keyframes gpu-draw { to { stroke-dashoffset: 0; } }
        @keyframes gpu-pulse-ring { 0% { box-shadow: 0 0 0 0 rgba(16,185,129,.35); } 100% { box-shadow: 0 0 0 14px rgba(16,185,129,0); } }
        .gpu-fade-up { animation: gpu-fade-up .35s ease both; }
        .gpu-pop { animation: gpu-pop .4s ease both; }
        .gpu-draw { stroke-dasharray: 30; stroke-dashoffset: 30; animation: gpu-draw .5s .15s ease forwards; }
        .gpu-ring { animation: gpu-pulse-ring 1.2s ease-out 1; }
        @media (prefers-reduced-motion: reduce) {
          .gpu-fade-up, .gpu-pop, .gpu-draw, .gpu-ring { animation: none !important; stroke-dashoffset: 0; }
        }
      `}</style>

      {/* Toast salin */}
      {copied && (
        <div className="gpu-pop fixed bottom-6 right-6 bg-emerald-600 text-white font-semibold text-xs px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2.5 z-50 border border-emerald-500">
          <Icon d={ICONS.check} sw={2.5} />
          <span>Broadcast berhasil disalin. Tinggal tempel ke Telegram.</span>
        </div>
      )}

      {/* Modal konfirmasi */}
      {(applyState === 'confirm' || applyState === 'applying') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="gpu-pop bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-bold text-slate-900">
              Terapkan harga {game.label}?
            </h3>
            <div className="text-xs text-slate-600 space-y-1.5">
              <p>
                <span className="font-semibold text-slate-800">
                  {validRows.length} produk
                </span>{' '}
                akan diperbarui{' '}
                {useHpp ? (
                  <>
                    dengan harga jual dari{' '}
                    <span className="font-semibold text-slate-800">
                      HPP GamesPanel
                    </span>{' '}
                    (Rp {formatRupiah(rate.rateTotal)} ÷ {rate.rateDivider}).
                  </>
                ) : (
                  <>
                    dengan markup{' '}
                    <span className="font-semibold text-slate-800">
                      {String(markupPct).replace('.', ',')}%
                    </span>
                    , pembulatan kelipatan {formatRupiah(roundStep)}.
                  </>
                )}
              </p>
              <p>
                {sheet
                  ? 'Harga Excel disimpan sebagai harga dasar/supplier, '
                  : ''}
                {useHpp
                  ? 'HPP GamesPanel sebagai harga jual.'
                  : 'hasil markup sebagai harga jual.'}
              </p>
              {errorCount > 0 && (
                <p className="text-amber-700 font-semibold">
                  {errorCount} baris bermasalah akan dilewati.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setApplyState('idle')}
                disabled={applyState === 'applying'}
                className={`${btnBase} bg-white border border-slate-300 text-slate-700 hover:bg-slate-50`}
              >
                Kembali
              </button>
              <button
                onClick={runApply}
                disabled={applyState === 'applying'}
                className={`${btnBase} bg-indigo-600 hover:bg-indigo-700 text-white`}
              >
                {applyState === 'applying' ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    <span>Menerapkan...</span>
                  </>
                ) : (
                  <span>Ya, Terapkan Harga</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-md overflow-hidden">
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-slate-50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="space-y-1">
            <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2.5">
              <span className="p-2 bg-indigo-50 rounded-lg text-indigo-600 border border-indigo-100/60 shadow-sm">
                <Icon d={ICONS.table} className="w-5 h-5" />
              </span>
              {useHpp ? 'Update Harga Free Fire (HPP GamesPanel)' : 'Update Harga Game via Excel'}
            </h2>
            <p className="text-xs text-slate-500">
              {useHpp
                ? 'Harga jual tampil langsung dari HPP GamesPanel dan ikut berubah saat rate diubah. Upload Excel bersifat opsional, hanya untuk membandingkan harga.'
                : 'Upload Excel, cek validasi, lihat preview, lalu terapkan harga dan siapkan broadcast Telegram.'}
            </p>
          </div>
          <button
            onClick={handleDownloadSample}
            className={`${btnBase} bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shrink-0`}
          >
            <Icon d={ICONS.download} />
            <span>Contoh Excel</span>
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-6">
          {/* Indikator alur */}
          <ol className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {steps.map((label, i) => {
              const done = i < currentStep;
              const active = i === currentStep;
              return (
                <li
                  key={label}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors duration-300 ${
                    active
                      ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                      : done
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-slate-50 text-slate-400'
                  }`}
                >
                  <span
                    className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      active
                        ? 'bg-indigo-600 text-white'
                        : done
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {done ? (
                      <Icon d={ICONS.check} className="w-3 h-3" sw={3} />
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span className="truncate">{label}</span>
                </li>
              );
            })}
          </ol>

          {/* Pilih game */}
          <div className="flex flex-wrap gap-2">
            {GAME_ORDER.map((id) => (
              <button
                key={id}
                onClick={() => {
                  setGameId(id);
                  setApplyState('idle');
                  setIgnoreErrors(false);
                }}
                className={`text-xs font-bold px-4 py-2 rounded-xl border transition-all duration-200 active:scale-95 ${
                  gameId === id
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                    : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                }`}
              >
                {GAME_CONFIG[id].label}
              </button>
            ))}
          </div>

          {/* Sumber harga jual Free Fire (HPP dari GamesPanel) */}
          {game.hppFromGamesPanel && (
            <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-bold text-slate-800">
                  Sumber Harga Jual Free Fire
                </p>
                <div className="inline-flex gap-1 p-1 bg-white rounded-lg border border-blue-100">
                  {[
                    ['hpp', 'HPP GamesPanel'],
                    ['markup', 'Excel + Markup'],
                  ].map(([k, l]) => (
                    <button
                      key={k}
                      onClick={() => setFfSource(k)}
                      className={`text-[11px] font-bold px-3 py-1.5 rounded-md transition-all ${
                        ffSource === k
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-slate-600 hover:text-blue-600'
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {useHpp ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-bold text-slate-700">
                        Total Pembelian Modal (IDR)
                      </label>
                      <input
                        type="number"
                        value={rate.rateTotal}
                        onChange={(e) =>
                          setRate((r) => ({
                            ...r,
                            rateTotal: Number(e.target.value),
                          }))
                        }
                        className="w-full bg-white border border-slate-300/80 rounded-xl px-3.5 py-2 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-bold text-slate-700">
                        Pembagi KiosGamer (DM / Unit)
                      </label>
                      <input
                        type="number"
                        value={rate.rateDivider}
                        onChange={(e) =>
                          setRate((r) => ({
                            ...r,
                            rateDivider: Number(e.target.value),
                          }))
                        }
                        className="w-full bg-white border border-slate-300/80 rounded-xl px-3.5 py-2 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                      />
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-600 space-y-1">
                    <p>
                      HPP per 1 unit: Rp{' '}
                      <span className="font-bold text-slate-800">
                        {ratePerUnit.toLocaleString('id-ID', {
                          maximumFractionDigits: 2,
                        })}
                      </span>
                      . Harga jual = unit produk x HPP per unit, dibulatkan ke
                      atas (sama dengan GamesPanel).
                    </p>
                    <p>
                      Harga Excel tetap disimpan sebagai harga dasar/supplier.
                      Pembulatan di bawah tidak dipakai untuk HPP.
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-slate-600">
                  Harga jual = harga Excel + markup, lalu dibulatkan.
                </p>
              )}

              <button
                onClick={() => setRate(loadFFRate())}
                className="text-[11px] font-bold text-blue-700 underline"
              >
                Muat ulang rate HPP tersimpan
              </button>
            </div>
          )}

          {/* Pengaturan markup */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">
                Markup {game.label} (%)
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={markupRaw}
                onChange={(e) =>
                  setMarkups((prev) => ({ ...prev, [gameId]: e.target.value }))
                }
                className={`w-full bg-slate-50 border rounded-xl px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none focus:bg-white focus:ring-2 transition-all shadow-sm ${
                  markupValid
                    ? 'border-slate-300/80 focus:border-indigo-500 focus:ring-indigo-500/20'
                    : 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                }`}
                placeholder="Contoh: 2,5"
              />
              {!markupValid && (
                <span className="text-[11px] text-red-600 font-semibold">
                  Masukkan angka 0 sampai 100.
                </span>
              )}
              {markupValid && markupNum === 0 && gameId !== 'FF' && (
                <span className="text-[11px] text-amber-700">
                  Markup masih 0%. Atur dulu sebelum menerapkan harga.
                </span>
              )}
              {useHpp && (
                <span className="text-[11px] text-slate-500">
                  Hanya dipakai untuk produk tanpa HPP (mis. kode PFF).
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">
                Pembulatan Harga Jual
              </label>
              <select
                value={roundStep}
                onChange={(e) => setRoundStep(Number(e.target.value))}
                className="w-full bg-slate-50 border border-slate-300/80 rounded-xl px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all cursor-pointer shadow-sm"
              >
                <option value={1}>Rupiah terdekat (1)</option>
                <option value={5}>Kelipatan 5</option>
                <option value={10}>Kelipatan 10</option>
                <option value={50}>Kelipatan 50</option>
                <option value={100}>Kelipatan 100</option>
              </select>
            </div>

            <div className="flex flex-col gap-2 justify-end">
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sortByDiamond}
                  onChange={(e) => setSortByDiamond(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                Urutkan dari harga termurah ke termahal
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={codeBlock}
                  onChange={(e) => setCodeBlock(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                Bungkus code block Telegram (```)
              </label>
            </div>
          </div>

          {/* Area upload */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`rounded-2xl border-2 border-dashed p-6 text-center transition-all duration-300 ${
              dragOver
                ? 'border-indigo-500 bg-indigo-50 scale-[1.01]'
                : 'border-slate-300 bg-slate-50 hover:border-indigo-400 hover:bg-indigo-50/40'
            }`}
          >
            {reading.active ? (
              <div className="gpu-fade-up max-w-sm mx-auto space-y-3">
                <p className="text-xs font-semibold text-slate-700">
                  Membaca file Excel...
                </p>
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className="h-full bg-indigo-600 rounded-full transition-all duration-300"
                    style={{ width: `${reading.progress}%` }}
                  />
                </div>
                <p className="text-[11px] text-slate-500">
                  {reading.progress}%
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="mx-auto w-11 h-11 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-indigo-600">
                  <Icon d={ICONS.upload} className="w-5 h-5" />
                </div>
                <p className="text-xs text-slate-600">
                  {sheet ? (
                    <>
                      File aktif:{' '}
                      <span className="font-bold text-slate-800">
                        {sheet.fileName}
                      </span>
                    </>
                  ) : (
                    useHpp
                      ? 'Opsional: upload Excel untuk membandingkan harga jual HPP dengan harga di Excel.'
                      : 'Tarik file Excel ke sini, atau pilih dari komputer / HP.'
                  )}
                </p>
                <p className="text-[11px] text-slate-400">
                  Kolom: Kode Produk, Produk, dan Harga (.xlsx, .xls, .csv,
                  maksimal 5 MB). Kode FREFIRE otomatis diubah ke kode FFP.
                </p>
                <input
                  ref={fileInputRef}
                  id="gpu-file"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files[0])}
                />
                <label
                  htmlFor="gpu-file"
                  className={`${btnBase} inline-flex bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer`}
                >
                  <Icon d={ICONS.upload} />
                  <span>{sheet ? 'Ganti File' : useHpp ? 'Upload Excel (Opsional)' : 'Upload Excel'}</span>
                </label>
              </div>
            )}
          </div>

          {fileError && (
            <div className="gpu-fade-up rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 flex items-start gap-2.5">
              <Icon d={ICONS.alert} className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{fileError}</span>
            </div>
          )}

          {/* HASIL SETELAH UPLOAD */}
          {analysis && (
            <div ref={previewRef} className="gpu-fade-up space-y-5 scroll-mt-4">
              {/* Sukses diterapkan */}
              {applyState === 'done' && (
                <div className="gpu-fade-up rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 flex items-center gap-4">
                  <span className="gpu-pop gpu-ring w-11 h-11 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        className="gpu-draw"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </span>
                  <div className="text-xs text-emerald-800">
                    <p className="font-bold text-sm">
                      Harga berhasil diterapkan
                    </p>
                    <p>
                      {validRows.length} produk {game.label} sudah diperbarui.
                      Anda bisa menyalin broadcast Telegram sekarang.
                    </p>
                  </div>
                </div>
              )}

              {applyState === 'failed' && (
                <div className="gpu-fade-up rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 flex items-center justify-between gap-3">
                  <span className="flex items-start gap-2.5">
                    <Icon
                      d={ICONS.alert}
                      className="w-4 h-4 mt-0.5 shrink-0"
                    />
                    <span>Gagal menerapkan harga: {applyError}</span>
                  </span>
                  <button
                    onClick={() => setApplyState('idle')}
                    className="font-bold underline shrink-0"
                  >
                    Tutup
                  </button>
                </div>
              )}

              {/* Statistik */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  {
                    k: 'Total Baris',
                    v: analysis.rows.length,
                    c: 'text-slate-900',
                  },
                  { k: 'Valid', v: validRows.length, c: 'text-emerald-600' },
                  {
                    k: 'Bermasalah',
                    v: errorCount,
                    c: errorCount ? 'text-red-600' : 'text-slate-400',
                  },
                  {
                    k: useHpp ? 'Sumber Harga' : 'Markup',
                    v: useHpp
                      ? 'HPP'
                      : markupPct === null
                      ? '-'
                      : `${String(markupPct).replace('.', ',')}%`,
                    c: 'text-indigo-600',
                  },
                ].map((s) => (
                  <div
                    key={s.k}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
                  >
                    <p className="text-[11px] font-semibold text-slate-500">
                      {s.k}
                    </p>
                    <p className={`text-xl font-bold mt-0.5 ${s.c}`}>{s.v}</p>
                  </div>
                ))}
              </div>

              {/* Validasi */}
              {errorCount > 0 ? (
                <div className="gpu-fade-up rounded-xl border border-red-200 bg-red-50 p-4 space-y-3">
                  <p className="text-xs font-bold text-red-700 flex items-center gap-2">
                    <Icon d={ICONS.alert} />
                    VALIDASI GAGAL
                  </p>
                  <div className="space-y-2 text-xs text-red-800">
                    {Object.keys(analysis.groups).map((type) => (
                      <div key={type}>
                        <p className="font-semibold">
                          {analysis.groups[type].length} {ERROR_LABELS[type]}:
                        </p>
                        <p className="font-mono text-[11px] text-red-700 break-words">
                          {analysis.groups[type].join(', ')}
                        </p>
                      </div>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-red-800 cursor-pointer pt-1">
                    <input
                      type="checkbox"
                      checked={ignoreErrors}
                      onChange={(e) => setIgnoreErrors(e.target.checked)}
                      className="rounded border-red-300 text-red-600 focus:ring-red-500"
                    />
                    Terapkan hanya baris valid ({validRows.length} produk) dan
                    abaikan {errorCount} baris bermasalah
                  </label>
                </div>
              ) : (
                <div className="gpu-fade-up rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-700 flex items-center gap-2">
                  <Icon d={ICONS.check} sw={2.5} />
                  Validasi berhasil. Semua {validRows.length} produk valid.
                </div>
              )}

              {/* Tombol aksi */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handlePreview}
                  className={`${btnBase} ${
                    view === 'table'
                      ? 'bg-slate-900 text-white'
                      : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Icon d={ICONS.table} />
                  <span>Preview</span>
                </button>
                <button
                  onClick={handlePreviewTelegram}
                  className={`${btnBase} ${
                    view === 'telegram'
                      ? 'bg-slate-900 text-white'
                      : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Icon d={ICONS.send} />
                  <span>Preview Telegram</span>
                </button>
                <button
                  onClick={handleCopy}
                  disabled={!telegramText}
                  className={`${btnBase} ${
                    copied
                      ? 'bg-emerald-600 text-white'
                      : 'bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                  }`}
                >
                  <Icon d={copied ? ICONS.check : ICONS.copy} />
                  <span>{copied ? 'Tersalin' : 'Salin Broadcast'}</span>
                </button>
                <button
                  onClick={() => setApplyState('confirm')}
                  disabled={!canApply}
                  className={`${btnBase} bg-indigo-600 hover:bg-indigo-700 text-white`}
                >
                  <Icon d={ICONS.check} />
                  <span>Terapkan Harga</span>
                </button>
                <button
                  onClick={handleCancel}
                  disabled={useHpp && !sheet}
                  className={`${btnBase} bg-white border border-slate-300 text-slate-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200`}
                >
                  <Icon d={ICONS.x} />
                  <span>{useHpp ? 'Hapus Excel' : 'Batal'}</span>
                </button>
              </div>

              {markupPct === null && !useHpp && (
                <p className="text-xs text-red-600 font-semibold">
                  Markup tidak valid, harga jual belum bisa dihitung.
                </p>
              )}
              {errorCount > 0 && !ignoreErrors && validRows.length > 0 && (
                <p className="text-[11px] text-slate-500">
                  Tombol Terapkan Harga aktif setelah semua masalah diperbaiki
                  atau Anda mencentang opsi "Terapkan hanya baris valid".
                </p>
              )}

              {/* PREVIEW TABEL */}
              {view === 'table' && (
                <div key="table" className="gpu-fade-up space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-900">
                      {game.title}
                    </h3>
                    <div className="flex gap-1.5">
                      {[
                        ['all', 'Semua'],
                        ['ok', 'Valid'],
                        ['error', 'Bermasalah'],
                      ].map(([k, l]) => (
                        <button
                          key={k}
                          onClick={() => setFilter(k)}
                          className={`text-[11px] font-bold px-3 py-1.5 rounded-lg border transition-all ${
                            filter === k
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 overflow-auto max-h-[420px] shadow-sm">
                    <table className="w-full min-w-[560px] text-xs border-collapse">
                      <thead>
                        <tr className="text-slate-600">
                          {gameCols.map(([h, a]) => (
                            <th
                              key={h}
                              className={`sticky top-0 z-10 bg-slate-50 border-b border-slate-200 px-4 py-3 font-bold ${a}`}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody key={filter}>
                        {visibleRows.length === 0 && (
                          <tr>
                            <td
                              colSpan={gameCols.length}
                              className="px-4 py-8 text-center text-slate-400"
                            >
                              Tidak ada data untuk ditampilkan.
                            </td>
                          </tr>
                        )}
                        {visibleRows.map((r) => (
                          <tr
                            key={`${r.line}-${r.code}`}
                            className={`gpu-fade-up border-b border-slate-100 transition-colors duration-200 ${
                              r.status === 'error'
                                ? 'bg-red-50/60 hover:bg-red-50'
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            <td className="px-4 py-2.5 font-mono font-bold text-slate-800">
                              {r.code || '-'}
                              {r.sourceCode && r.sourceCode !== r.code && (
                                <span className="block text-[10px] font-normal text-slate-400">
                                  Excel: {r.sourceCode}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-slate-700">
                              {r.diamond !== null
                                ? `${r.diamond} ${game.unit}`
                                : '-'}
                            </td>
                            {(!useHpp || sheet) && (
                              <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-600">
                                {r.base !== null ? formatRupiah(r.base) : '-'}
                              </td>
                            )}
                            <td className="px-4 py-2.5 text-right font-mono tabular-nums font-bold text-slate-900">
                              {r.sell !== null ? formatRupiah(r.sell) : '-'}
                            </td>
                            {useHpp && sheet && (
                              <td
                                className={`px-4 py-2.5 text-right font-mono tabular-nums ${
                                  r.diff === null || r.diff === 0
                                    ? 'text-slate-400'
                                    : r.diff > 0
                                    ? 'text-emerald-600'
                                    : 'text-red-600'
                                }`}
                              >
                                {r.diff === null
                                  ? '-'
                                  : `${r.diff > 0 ? '+' : r.diff < 0 ? '-' : ''}${formatRupiah(Math.abs(r.diff))}`}
                              </td>
                            )}
                            <td className="px-4 py-2.5">
                              {r.status === 'ok' && (
                                <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 text-[11px] font-semibold">
                                  Valid{r.hpp ? ' · HPP' : ''}
                                </span>
                              )}
                              {r.status === 'warn' && (
                                <span
                                  title={r.message}
                                  className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 text-[11px] font-semibold"
                                >
                                  {r.message}
                                </span>
                              )}
                              {r.status === 'error' && (
                                <span
                                  title={r.message}
                                  className="inline-flex items-center rounded-full bg-red-50 text-red-700 border border-red-200 px-2.5 py-0.5 text-[11px] font-semibold"
                                >
                                  {r.message}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    {useHpp ? (
                      <>
                        Harga Jual = HPP GamesPanel (unit produk x HPP per unit
                        dari rate di atas).
                        {sheet
                          ? ' Harga Excel hanya pembanding. Selisih = Harga Jual dikurangi Harga Excel (hijau = jual di atas Excel, merah = di bawah). Produk di Excel yang tidak punya HPP (mis. kode PFF) memakai harga Excel + markup.'
                          : ' Upload Excel (opsional) bila ingin membandingkan dengan harga supplier.'}
                      </>
                    ) : (
                      'Harga Dasar = harga dari Excel (supplier, tidak diubah). Harga Jual = harga dasar + markup, lalu dibulatkan.'
                    )}
                  </p>
                </div>
              )}

              {/* PREVIEW TELEGRAM */}
              {view === 'telegram' && (
                <div key="telegram" className="gpu-fade-up space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-900">
                      Preview Broadcast Telegram
                    </h3>
                    <span className="text-[11px] text-slate-500">
                      Lebar baris: {maxLineWidth} karakter
                      {maxLineWidth > 38 &&
                        ' (bisa terpotong di layar HP kecil)'}
                    </span>
                  </div>

                  <div className="rounded-xl bg-slate-900 border border-slate-800 shadow-inner p-4 overflow-auto max-h-[460px]">
                    {telegramText ? (
                      <pre className="text-emerald-400 font-mono text-xs leading-relaxed whitespace-pre select-all selection:bg-emerald-900/60 selection:text-emerald-200">
                        {telegramText}
                      </pre>
                    ) : (
                      <p className="text-slate-500 italic text-xs">
                        Belum ada produk valid untuk dibroadcast.
                      </p>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Hasil sudah memakai spasi monospace. Saat ditempel ke
                    Telegram, tanda ``` akan berubah menjadi code block sehingga
                    kolom tetap sejajar di desktop, web, dan mobile.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==========================================================
// TEMPLATE HARGA DIGIFLAZZ
// Alur: upload Excel / tempel teks -> preview Telegram -> unduh template
// ==========================================================

const TPL_STYLES = `
  @keyframes tpl-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
  @keyframes tpl-line { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: none; } }
  @keyframes tpl-draw { to { stroke-dashoffset: 0; } }
  @keyframes tpl-pop { 0% { transform: scale(.55); opacity: 0; } 70% { transform: scale(1.1); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
  @keyframes tpl-ring { 0% { box-shadow: 0 0 0 0 rgba(79,70,229,.38); } 100% { box-shadow: 0 0 0 12px rgba(79,70,229,0); } }
  @keyframes tpl-ready { 0% { box-shadow: 0 0 0 0 rgba(16,185,129,.5); } 100% { box-shadow: 0 0 0 16px rgba(16,185,129,0); } }
  @keyframes tpl-sheen { from { transform: translateX(-130%); } to { transform: translateX(330%); } }
  @keyframes tpl-blink { 50% { opacity: 0; } }
  @keyframes tpl-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }

  .tpl-rise { animation: tpl-rise .45s cubic-bezier(.2,.7,.2,1) both; }
  .tpl-line { animation: tpl-line .32s ease both; }
  .tpl-pop { animation: tpl-pop .4s ease both; }
  .tpl-draw { stroke-dasharray: 26; stroke-dashoffset: 26; animation: tpl-draw .45s .1s ease forwards; }
  .tpl-ring { animation: tpl-ring 1.6s ease-out infinite; }
  .tpl-ready { animation: tpl-ready 1.3s ease-out 2; }
  .tpl-caret { animation: tpl-blink 1s steps(1) infinite; }
  .tpl-float { animation: tpl-float 3.2s ease-in-out infinite; }

  .tpl-sheen { position: relative; overflow: hidden; }
  .tpl-sheen::after {
    content: ''; position: absolute; top: 0; bottom: 0; left: 0; width: 30%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.35), transparent);
    transform: translateX(-130%); pointer-events: none;
  }
  .tpl-sheen:not(:disabled):hover::after { animation: tpl-sheen .8s ease; }

  .tpl-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
  .tpl-scroll::-webkit-scrollbar-thumb { background: rgba(148,163,184,.35); border-radius: 8px; }
  .tpl-scroll::-webkit-scrollbar-thumb:hover { background: rgba(148,163,184,.6); }

  @media (prefers-reduced-motion: reduce) {
    .tpl-rise, .tpl-line, .tpl-pop, .tpl-ring, .tpl-ready, .tpl-caret, .tpl-float { animation: none !important; }
    .tpl-draw { animation: none !important; stroke-dashoffset: 0; }
    .tpl-sheen::after { display: none; }
  }
`;

const TPL_ICONS = {
  tag: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
  book: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  chevron: 'M19 9l-7 7-7-7',
  trash:
    'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  bolt: 'M13 10V3L4 14h7v7l9-11h-7z',
  doc: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  sparkle:
    'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
};

const TPL_CAPTIONS = [
  {
    id: 'NONE',
    label: 'Tanpa caption',
    on: 'bg-white text-slate-900 shadow-sm',
  },
  {
    id: 'TURUN',
    label: 'Turun harga',
    on: 'bg-emerald-600 text-white shadow-sm',
  },
  {
    id: 'NAIK',
    label: 'Naik harga',
    on: 'bg-rose-600 text-white shadow-sm',
  },
];

const TPL_SAMPLE_TEXT = 'FREFIRE5 798\nFREFIRE10 1590\nFREFIRE0 11901\nFREFIRE50 6347\nFREFIRE100 12695';

// Hitung naik secara halus dari nilai lama ke nilai baru
const useCountUp = (target, duration = 650) => {
  const [value, setValue] = useState(target);
  const shown = useRef(target);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce || shown.current === target) {
      shown.current = target;
      setValue(target);
      return undefined;
    }

    const from = shown.current;
    const start = performance.now();
    let raf;

    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = Math.round(from + (target - from) * eased);
      shown.current = next;
      setValue(next);
      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return value;
};

const StatTile = ({ label, value, prefix = '', empty }) => {
  const shown = useCountUp(value || 0);
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3">
      <div className="text-[11px] font-medium text-slate-500">{label}</div>
      <div className="mt-0.5 text-lg font-black tabular-nums text-slate-900">
        {empty ? '-' : `${prefix}${formatRupiah(shown)}`}
      </div>
    </div>
  );
};

// Susun daftar harga -> { text, items, skipped }
// items: [{ sku, price }]  (dipakai juga untuk template Digiflazz)
const buildTemplateData = (rawRows, { title, prefix, sortByPrice }) => {
  const items = [];
  const skipped = [];
  const seen = new Set();

  rawRows.forEach((r) => {
    const code = normCode(r.code);
    if (!code && isBlank(r.price)) return; // baris kosong

    if (!code) {
      skipped.push({ label: `Baris ${r.line}`, reason: 'kode produk kosong' });
      return;
    }
    const p = parsePrice(r.price);
    if (p.error) {
      skipped.push({ label: code, reason: p.message.toLowerCase() });
      return;
    }
    if (seen.has(code)) {
      skipped.push({
        label: code,
        reason: 'kode duplikat (yang dipakai baris pertama)',
      });
      return;
    }
    seen.add(code);
    items.push({ sku: code, price: Math.max(1, Math.round(p.value)) });
  });

  if (sortByPrice) {
    items.sort(
      (a, b) =>
        a.price - b.price ||
        a.sku.localeCompare(b.sku, 'en', { numeric: true })
    );
  }

  if (!items.length) return { text: '', items, skipped };

  const prices = items.map((i) => formatRupiah(i.price));
  const codeW = Math.max(...items.map((i) => i.sku.length));
  const priceW = Math.max(...prices.map((p) => p.length));
  const lines = items.map(
    (it, i) => `${it.sku.padEnd(codeW, ' ')}  ${prices[i].padStart(priceW, ' ')}`
  );
  const lineW = Math.max(...lines.map((l) => l.length));

  let text = '';
  if (prefix === 'TURUN') text += 'INFO: TURUN HARGA\n\n';
  else if (prefix === 'NAIK') text += 'INFO: NAIK HARGA\n\n';
  if (title) text += `${title}\n${'═'.repeat(Math.max(title.length, lineW))}\n\n`;
  text += lines.join('\n');

  return { text, items, skipped };
};

// ----------------------------------------------------------
// PANEL TAMPILAN (presentational): semua state ada di TelegramUpload
// ----------------------------------------------------------
const DigiflazzTemplatePanel = ({
  categoryName,
  setCategoryName,
  prefixCaption,
  setPrefixCaption,
  sortByPrice,
  setSortByPrice,
  inputText,
  setInputText,
  onGenerateText,
  onLoadSample,
  sourceLabel,
  reading,
  inputError,
  onFile,
  outputText,
  items,
  skipped,
  genId,
  copied,
  onCopy,
  onDownload,
  onReset,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const hasData = items.length > 0;
  const outLines = outputText ? outputText.split('\n') : [];
  const headerLines = Math.max(0, outLines.length - items.length);

  const prices = items.map((i) => i.price);
  const minPrice = hasData ? Math.min(...prices) : 0;
  const maxPrice = hasData ? Math.max(...prices) : 0;
  const inputLineCount = inputText.split('\n').filter((l) => l.trim()).length;

  const focusRing =
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';

  const openPicker = () => fileRef.current && fileRef.current.click();

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    onFile(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5 font-sans relative text-slate-800">
      <style>{TPL_STYLES}</style>

      {/* ---------- Header ---------- */}
      <div className="tpl-rise rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-white to-indigo-50/60 p-5 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <span className="tpl-float mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-md shadow-indigo-600/25">
              <Icon d={TPL_ICONS.tag} className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-lg sm:text-xl font-black text-slate-900 leading-tight">
                Template Harga Digiflazz
              </h2>
              <p className="mt-1 text-xs sm:text-[13px] text-slate-500 max-w-md">
                Ubah daftar harga menjadi teks siap kirim ke Telegram dan file
                template yang bisa langsung diunggah ke Digiflazz.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Input + Preview ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Kolom kiri: pengaturan + sumber data */}
        <div
          className="tpl-rise rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm space-y-5"
          style={{ animationDelay: '140ms' }}
        >
          <div className="space-y-1.5">
            <label
              htmlFor="tpl-title"
              className="text-xs font-bold text-slate-700"
            >
              Judul kategori / banner
            </label>
            <input
              id="tpl-title"
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value.toUpperCase())}
              placeholder="Contoh: TOP UP FREE FIRE"
              className="w-full rounded-xl border border-slate-300/80 bg-slate-50 px-3.5 py-2.5 text-xs font-bold text-slate-800 shadow-sm outline-none transition-all placeholder:font-normal focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>

          <div className="space-y-1.5">
            <span className="text-xs font-bold text-slate-700">
              Caption tambahan
            </span>
            <div
              role="radiogroup"
              aria-label="Caption tambahan"
              className="grid grid-cols-3 gap-1 rounded-xl border border-slate-200 bg-slate-100 p-1"
            >
              {TPL_CAPTIONS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={prefixCaption === c.id}
                  onClick={() => setPrefixCaption(c.id)}
                  className={`rounded-lg px-2 py-2 text-xs font-bold transition-all duration-200 ${focusRing} ${
                    prefixCaption === c.id
                      ? c.on
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div>
              <p className="text-xs font-bold text-slate-700">
                Urutkan dari harga termurah
              </p>
              <p className="text-[11px] text-slate-500">
                Berlaku untuk preview Telegram dan file template.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={sortByPrice}
              aria-label="Urutkan dari harga termurah"
              onClick={() => setSortByPrice(!sortByPrice)}
              className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${focusRing} ${
                sortByPrice ? 'bg-indigo-600' : 'bg-slate-300'
              }`}
            >
              <span
                className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-300 ${
                  sortByPrice ? 'translate-x-5' : ''
                }`}
              />
            </button>
          </div>

          {/* Dropzone */}
          <div className="space-y-1.5">
            <span className="text-xs font-bold text-slate-700">
              Upload file Excel
            </span>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => onFile(e.target.files && e.target.files[0])}
            />
            <div
              role="button"
              tabIndex={0}
              onClick={openPicker}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPicker();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-5 text-center transition-all duration-300 ${focusRing} ${
                dragOver
                  ? 'scale-[1.01] border-indigo-500 bg-indigo-50'
                  : 'border-slate-300 bg-slate-50 hover:border-indigo-400 hover:bg-indigo-50/40'
              }`}
            >
              {reading.active ? (
                <div className="mx-auto max-w-[220px]">
                  <p className="mb-2 text-xs font-bold text-slate-600">
                    Membaca file... {reading.progress}%
                  </p>
                  <div className="h-1.5 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-indigo-500 transition-all duration-300"
                      style={{ width: `${reading.progress}%` }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <span
                    className={`mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 ${
                      dragOver
                        ? '-translate-y-1 bg-indigo-600 text-white'
                        : 'bg-white text-indigo-600 shadow-sm border border-slate-200'
                    }`}
                  >
                    <Icon d={ICONS.upload} className="w-5 h-5" />
                  </span>
                  {sourceLabel && hasData ? (
                    <p className="tpl-pop text-xs font-bold text-slate-800">
                      {sourceLabel}
                      <span className="ml-1.5 font-medium text-emerald-600">
                        {items.length} produk terbaca
                      </span>
                    </p>
                  ) : (
                    <p className="text-xs font-bold text-slate-700">
                      Tarik file ke sini, atau klik untuk memilih
                    </p>
                  )}
                  <p className="mt-0.5 text-[11px] text-slate-400">
                    .xlsx, .xls, atau .csv, maksimal 5 MB
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Teks manual */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label
                htmlFor="tpl-text"
                className="text-xs font-bold text-slate-700"
              >
                Atau tempel data teks (kode dan harga)
              </label>
              <button
                type="button"
                onClick={onLoadSample}
                className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-bold text-indigo-600 hover:bg-indigo-50 ${focusRing}`}
              >
                <Icon d={TPL_ICONS.sparkle} className="w-3.5 h-3.5" />
                Isi contoh
              </button>
            </div>
            <textarea
              id="tpl-text"
              rows="6"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={'FREFIRE5 798\nFREFIRE10 1590'}
              spellCheck={false}
              className="w-full resize-none rounded-xl border border-slate-300/80 bg-slate-50 p-3.5 font-mono text-xs shadow-sm outline-none transition-all placeholder:text-slate-300 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-500/20"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onGenerateText}
                className={`tpl-sheen flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 py-2.5 text-xs font-bold text-white shadow-sm transition-all duration-200 hover:bg-indigo-700 hover:shadow active:scale-95 ${focusRing}`}
              >
                <Icon d={TPL_ICONS.bolt} className="w-4 h-4" />
                Generate dari teks
                {inputLineCount > 0 && (
                  <span className="rounded-md bg-white/20 px-1.5 text-[10px]">
                    {inputLineCount} baris
                  </span>
                )}
              </button>
              {(hasData || inputText) && (
                <button
                  type="button"
                  onClick={onReset}
                  title="Hapus semua data"
                  className={`inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-bold text-slate-600 transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-600 active:scale-95 ${focusRing}`}
                >
                  <Icon d={TPL_ICONS.trash} className="w-4 h-4" />
                  Reset
                </button>
              )}
            </div>

            {inputError && (
              <p
                role="alert"
                className="tpl-rise flex items-start gap-1.5 text-xs font-medium text-red-600"
              >
                <Icon d={ICONS.alert} className="mt-0.5 w-3.5 h-3.5 shrink-0" />
                {inputError}
              </p>
            )}
          </div>
        </div>

        {/* Kolom kanan: preview Telegram */}
        <div
          className="tpl-rise space-y-4"
          style={{ animationDelay: '210ms' }}
        >
          <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 shadow-lg">
            <div className="flex items-center gap-2 border-b border-slate-700/60 bg-slate-800/80 px-4 py-2.5">
              <span className="flex gap-1.5" aria-hidden="true">
                <i className="h-2.5 w-2.5 rounded-full bg-rose-400/90" />
                <i className="h-2.5 w-2.5 rounded-full bg-amber-300/90" />
                <i className="h-2.5 w-2.5 rounded-full bg-emerald-400/90" />
              </span>
              <span className="ml-2 text-[11px] font-medium text-slate-300">
                Preview Telegram
              </span>
              <span className="ml-auto text-[11px] tabular-nums text-slate-500">
                {outLines.length > 0 ? `${outLines.length} baris` : 'kosong'}
              </span>
            </div>

            <div className="tpl-scroll h-[340px] overflow-auto p-4">
              {outLines.length > 0 ? (
                <div className="select-all font-mono text-xs leading-relaxed selection:bg-emerald-900/60">
                  {outLines.map((line, i) => (
                    <div
                      key={`${genId}-${i}`}
                      className={`tpl-line whitespace-pre ${
                        i < headerLines
                          ? 'font-bold text-slate-200'
                          : 'text-emerald-300'
                      }`}
                      style={{ animationDelay: `${Math.min(i * 14, 450)}ms` }}
                    >
                      {line || '\u00A0'}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-700 bg-slate-800 text-slate-500">
                    <Icon d={TPL_ICONS.doc} className="w-6 h-6" />
                  </span>
                  <p className="max-w-[230px] text-xs leading-relaxed text-slate-500">
                    Belum ada data. Upload Excel atau tempel teks, hasilnya
                    muncul di sini
                    <span className="tpl-caret ml-0.5 text-emerald-400">|</span>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Statistik */}
          <div className="grid grid-cols-3 gap-3">
            <StatTile label="Jumlah produk" value={items.length} empty={!hasData} />
            <StatTile label="Termurah" prefix="Rp " value={minPrice} empty={!hasData} />
            <StatTile label="Termahal" prefix="Rp " value={maxPrice} empty={!hasData} />
          </div>

          {/* Aksi */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onCopy}
              disabled={!hasData}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-xs font-bold shadow-sm transition-all duration-200 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 ${focusRing} ${
                copied
                  ? 'bg-emerald-600 text-white'
                  : 'border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              <Icon d={copied ? ICONS.check : ICONS.copy} className="w-4 h-4" />
              {copied ? 'Tersalin' : 'Salin hasil'}
            </button>
            <button
              type="button"
              onClick={onDownload}
              disabled={!hasData}
              className={`tpl-sheen inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-xs font-bold text-white shadow-sm transition-all duration-200 hover:bg-emerald-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 ${focusRing}`}
            >
              <Icon d={ICONS.download} className="w-4 h-4" />
              Unduh template
            </button>
          </div>

          {skipped.length > 0 && (
            <details className="tpl-rise group rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
              <summary className="flex cursor-pointer list-none [&::-webkit-details-marker]:hidden items-center justify-between gap-2 font-bold">
                <span className="flex items-center gap-2">
                  <Icon d={ICONS.alert} className="w-4 h-4 text-amber-600" />
                  {skipped.length} baris dilewati
                </span>
                <Icon
                  d={TPL_ICONS.chevron}
                  className="w-3.5 h-3.5 transition-transform duration-200 group-open:rotate-180"
                />
              </summary>
              <ul className="tpl-scroll mt-2 max-h-32 space-y-0.5 overflow-auto">
                {skipped.map((s, i) => (
                  <li key={`${s.label}-${i}`}>
                    <span className="font-mono font-bold">{s.label}</span>:{' '}
                    {s.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

// ==========================================================
// UPDATE HARGA REGULER (THREE REGULER + INDOSAT REGULER)
// Alur: atur persen (naik / turun) -> hasil otomatis dihitung ->
//       preview tabel & Telegram -> salin broadcast / unduh Excel hasil.
//       Data produk sudah tertanam (REG_BUILTIN), tanpa upload Excel.
//
//   HTT = Three Reguler   : harga dasar = kolom "HARGA 3 SAKTI"
//   HI  = Indosat Reguler : harga dasar = "amount" katalog Indosat
//
// Rumus (sama dengan Excel yang dikirim, hanya persennya bisa diubah):
//   HASIL       = HARGA DASAR x PERSEN
//   HARGA JUAL  = HARGA DASAR + HASIL
//   PERSEN positif = NAIK, PERSEN negatif = TURUN
// Bawaan: Three +0,1% (naik) dan Indosat -1% (turun) = sama dengan Excel asli.
// ==========================================================
const REG_SETTINGS_KEY = 'regulerPriceUpdateSettings';
const REG_ORDER = ['HTT', 'HI'];

const REG_CONFIG = {
  HTT: {
    id: 'HTT',
    label: 'Three Reguler',
    prefix: 'HTT',
    codePattern: /^HTT\d+$/,
    prefixPattern: /^HTT/,
    typoPattern: null,
    defaultTitle: 'THREE REGULER',
    listGap: false,
    defaultFooter: [
      '• 100% Valid',
      '• Rekon H-0',
      '• Latency 0–5 Detik',
      '• No Mix Pulsa Transfer',
      '• Jalur RITA X Modchan',
      '',
      'Website : https://ifyone.id',
      'Digiflazz : https://digiflazz.com/seller/o6n0Vo',
      'Telegram : https://t.me/IFYOneofficial',
      'CS : @csifyone',
    ].join('\n'),
    defaultDir: 'up',
    defaultPercent: '0,1',
    sample: [
      ['HTT1', 1329],
      ['HTT2', 2312],
      ['HTT3', 3300],
    ],
  },
  HI: {
    id: 'HI',
    label: 'Indosat Reguler',
    prefix: 'HI',
    codePattern: /^HI\d+$/,
    prefixPattern: /^HI/,
    // salah ketik yang pernah muncul di Excel (HP30 seharusnya HI30)
    typoPattern: /^HP\d+$/,
    defaultTitle: 'INDOSAT REGULER',
    listGap: true,
    defaultFooter: [
      'INFO PRODUCT',
      '• 100% Valid & Aman',
      '• MOBO',
      '• Latency Detikan',
      '• Support Reply Chip',
      '',
      'Website : https://ifyone.id',
      'Digiflazz : https://digiflazz.com/seller/o6n0Vol',
      'Customer Service : @csifyone',
    ].join('\n'),
    defaultDir: 'down',
    defaultPercent: '1',
    sample: [
      ['HI5', 6750],
      ['HI10', 11750],
      ['HI12', 13100],
    ],
  },
};

// ----------------------------------------------------------
// DATA BAWAAN (tidak perlu upload Excel)
//   HTT : [kode, harga dasar Three] (hanya denom reguler yang dijual)
//   HI  : [kode, harga dasar (amount katalog Indosat), Hrg. Jual lama]
// Mau ganti harga dasar? Cukup edit angka di daftar ini.
// ----------------------------------------------------------
const REG_BUILTIN = {
  HTT: [
    ['HTT5', 5155], ['HTT10', 10147], ['HTT15', 14645], ['HTT20', 19646],
    ['HTT25', 24564], ['HTT30', 29275], ['HTT35', 34268], ['HTT40', 39100],
    ['HTT50', 48355], ['HTT60', 57895], ['HTT70', 67450], ['HTT75', 72325],
    ['HTT80', 77000], ['HTT90', 87940], ['HTT100', 93670], ['HTT125', 117150],
    ['HTT150', 140400], ['HTT200', 187850], ['HTT250', 243700], ['HTT300', 292250],
    ['HTT500', 481600],
  ],
  HI: [
    ['HI5', 6750, 6615], ['HI10', 11750, 11515],
    ['HI12', 13100, 12838], ['HI15', 16000, 15680],
    ['HI20', 21000, 20580], ['HI25', 26000, 25480],
    ['HI30', 31000, 30380], ['HI35', 36000, 35280],
    ['HI40', 40500, 39690], ['HI45', 45500, 44590],
    ['HI50', 50500, 49490], ['HI55', 55500, 54390],
    ['HI60', 60500, 59290], ['HI65', 65500, 64190],
    ['HI70', 70500, 69090], ['HI80', 80500, 78890],
    ['HI85', 85500, 83790], ['HI90', 90500, 88690],
    ['HI100', 100500, 98490], ['HI105', 105500, 103390],
    ['HI115', 115500, 113190], ['HI125', 125500, 122990],
    ['HI150', 150500, 147490], ['HI200', 200500, 196490],
    ['HI300', 300500, 294490], ['HI400', 400500, 392490],
    ['HI500', 500500, 490490],
  ],
};

// Bentuk datanya sama dengan hasil baca Excel, jadi perhitungan,
// validasi, preview, broadcast dan unduh Excel tidak berubah.
const makeRegBuiltinSheet = (id) => {
  const hasOld = id === 'HI';
  const rows = REG_BUILTIN[id].map(([code, base, old], i) => ({
    line: i + 2,
    code,
    baseRaw: base,
    name: '',
    dnm: '',
    lookupError: '',
    oldRaw: hasOld ? old : '',
    activeRaw: '',
  }));
  return {
    fileName: 'Data bawaan',
    parsed: {
      rows,
      meta: {
        sheetName: 'Data bawaan',
        baseSource:
          id === 'HI'
            ? 'Amount katalog Indosat (dnmcode)'
            : 'Daftar harga Three (data bawaan)',
        hasName: false,
        hasOld,
      },
    },
  };
};

// Kode -> milik operator mana (null = tidak dikenal)
const regOperatorOf = (code) => {
  if (/^HTT\d+$/.test(code)) return 'HTT';
  if (/^H[IP]\d+$/.test(code)) return 'HI';
  return null;
};

const REG_ERROR_LABELS = {
  kode_kosong: 'baris tanpa kode produk',
  duplikat: 'kode produk duplikat',
  operator_salah: 'kode milik operator lain',
  kode_tidak_dikenal: 'kode produk tidak dikenal',
  nonaktif: 'produk tidak aktif',
  harga_kosong: 'harga dasar kosong',
  harga_invalid: 'harga bukan angka / tidak valid',
  dnm_tidak_ditemukan: 'dnmcode tidak ada di katalog',
};

const REG_ROUND_STEPS = [
  [0, 'Tanpa pembulatan'],
  [1, 'Rupiah terdekat (1)'],
  [5, 'Kelipatan 5'],
  [10, 'Kelipatan 10'],
  [50, 'Kelipatan 50'],
  [100, 'Kelipatan 100'],
];
const REG_ROUND_MODES = [
  ['nearest', 'Terdekat'],
  ['up', 'Ke atas'],
  ['down', 'Ke bawah'],
];
const REG_PERCENT_CHIPS = ['0,1', '0,5', '1', '1,5', '2', '2,5', '3', '5'];

const regNorm = (c) =>
  String(c === null || c === undefined ? '' : c)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

// ----- Format angka (koma desimal, titik ribuan) -----
const formatDec = (n, maxDp = 3) => {
  if (n === null || n === undefined || isNaN(n)) return '-';
  const abs = Math.abs(n);
  let s = abs.toFixed(maxDp);
  if (s.indexOf('.') >= 0) s = s.replace(/\.?0+$/, '');
  const parts = s.split('.');
  const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const out = `${int}${parts[1] ? ',' + parts[1] : ''}`;
  return n < 0 && out !== '0' ? `-${out}` : out;
};

const formatSigned = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '-';
  if (Math.abs(n) < 1e-9) return '0';
  return n > 0 ? `+${formatDec(n)}` : formatDec(n);
};

const formatPct = (p) => {
  if (p === null || p === undefined || isNaN(p)) return '-';
  if (Math.abs(p) < 1e-9) return '0%';
  return `${p > 0 ? '+' : '-'}${formatDec(Math.abs(p), 4)}%`;
};

// Persen dari kolom input. Arah (naik/turun) ditentukan tombol, bukan tanda.
const parseRegPercent = (raw, dir) => {
  const s = String(raw === null || raw === undefined ? '' : raw)
    .trim()
    .replace(',', '.');
  if (s === '') return { error: 'Isi persentase terlebih dahulu.' };
  if (!/^\d+(\.\d+)?$/.test(s))
    return { error: 'Masukkan angka saja, contoh: 0,1 atau 2,5.' };
  const n = Number(s);
  if (!isFinite(n)) return { error: 'Persentase tidak valid.' };
  if (n > 100) return { error: 'Maksimal 100%.' };
  if (dir === 'down' && n >= 100)
    return { error: 'Persen turun harus kurang dari 100%.' };
  return { value: n };
};

// Pembulatan harga (step 0 = tanpa pembulatan)
const roundRegPrice = (raw, step, mode) => {
  if (!(step > 0)) return raw;
  const q = raw / step;
  let n;
  if (mode === 'up') n = Math.ceil(q - 1e-9);
  else if (mode === 'down') n = Math.floor(q + 1e-9);
  else n = Math.round(q + 1e-9);
  return n * step;
};

// HASIL = dasar x persen ; HARGA JUAL = dasar + hasil (lalu dibulatkan)
// pctSigned: +0,1 = naik 0,1% ; -1 = turun 1%
const calcReg = (base, pctSigned, step, mode) => {
  const hasil = Math.round(((base * pctSigned) / 100) * 1e6) / 1e6;
  const exact = Math.round((base + hasil) * 1e6) / 1e6;
  const sell = roundRegPrice(exact, step, mode);
  return { hasil: hasil === 0 ? 0 : hasil, exact, sell };
};

const regIsInactive = (v) => {
  if (typeof v === 'number') return v === 0;
  const s = regNorm(v);
  return [
    '0',
    'false',
    'tidak',
    'tidak aktif',
    'nonaktif',
    'non aktif',
    'non-aktif',
    'off',
    'no',
  ].includes(s);
};

const regCompare = (a, b) => {
  const pa = a.sell === null || a.sell === undefined ? Infinity : a.sell;
  const pb = b.sell === null || b.sell === undefined ? Infinity : b.sell;
  if (pa !== pb) return pa < pb ? -1 : 1;
  return String(a.code).localeCompare(String(b.code), 'en', { numeric: true });
};

// Teks broadcast Telegram.
// Bagian atas (judul + list harga) dibuat monospace & rata kanan supaya
// kolom harga sejajar. Footer (info produk & link) ada DI LUAR code block
// agar link tetap bisa diklik di Telegram.
// rows sudah diurutkan oleh pemanggil. Hasil: { text, width }.
const REG_TG_SEP_WIDTH = 20;

const buildRegTelegramText = ({
  title,
  rows,
  codeBlock,
  caption,
  footer,
  listGap,
}) => {
  if (!rows.length) return { text: '', width: 0 };

  const prices = rows.map((r) => formatDec(r.sell, 3));
  const codeW = Math.max(...rows.map((r) => r.code.length));
  const priceW = Math.max(...prices.map((p) => p.length));
  const lines = rows.map(
    (r, i) =>
      `${r.code.padEnd(codeW, ' ')}  ${prices[i].padStart(priceW, ' ')}`
  );
  const listW = Math.max(...lines.map((l) => l.length));

  const head = [];
  if (caption === 'TURUN') head.push('INFO: TURUN HARGA', '');
  else if (caption === 'NAIK') head.push('INFO: NAIK HARGA', '');
  if (title) {
    head.push(title, '═'.repeat(Math.max(REG_TG_SEP_WIDTH, title.length)));
  }
  head.push('List Harga:', '⸻');
  if (listGap) head.push('');

  const block = [...head, ...lines].join('\n');
  const width = Math.max(listW, ...head.map((l) => l.length));
  const foot = (footer || '').replace(/\s+$/, '').replace(/^\s*\n/, '');

  const main = codeBlock ? '```\n' + block + '\n```' : block;
  return { text: foot ? `${main}\n\n${foot}` : main, width };
};

// Susun file Excel hasil (rumus tetap hidup: ubah PERSEN di Excel -> hasil ikut berubah)
const buildRegWorkbook = ({
  cfg,
  rows,
  pctSigned,
  roundStep,
  roundMode,
  meta,
  fileName,
  title,
}) => {
  const hasOld = !!meta.hasOld;
  const headers = [
    'KODE',
    'HARGA DASAR',
    'PERSEN',
    'HASIL',
    'HARGA SEBELUM PEMBULATAN',
    'HARGA JUAL',
  ];
  if (hasOld) headers.push('HARGA LAMA', 'SELISIH');
  headers.push('KETERANGAN');

  const ws = {};
  const put = (r, c, cell) => {
    ws[XLSX.utils.encode_cell({ r, c })] = cell;
  };
  const S = (v) => ({ t: 's', v: String(v) });
  const N = (v, z, f) => {
    const cell = { t: 'n', v };
    if (z) cell.z = z;
    if (f) cell.f = f;
    return cell;
  };
  const moneyFmt = (v) => (Number.isInteger(v) ? '#,##0' : '#,##0.0##');
  // format HARGA JUAL: bulat -> tanpa desimal, tanpa pembulatan -> boleh desimal
  const sellFmt = roundStep > 0 ? '#,##0' : '#,##0.0##';
  const roundFormula = (ref) => {
    if (!(roundStep > 0)) return ref;
    if (roundMode === 'up') return `CEILING(${ref},${roundStep})`;
    if (roundMode === 'down') return `FLOOR(${ref},${roundStep})`;
    return roundStep === 1 ? `ROUND(${ref},0)` : `MROUND(${ref},${roundStep})`;
  };

  headers.forEach((h, c) => put(0, c, S(h)));

  const pctCell = Number((pctSigned / 100).toPrecision(12));
  const noteCol = headers.length - 1;

  rows.forEach((r, i) => {
    const n = i + 2; // nomor baris Excel
    const rr = i + 1;
    put(rr, 0, S(r.code));
    put(rr, 1, N(r.base, moneyFmt(r.base)));
    put(rr, 2, N(pctCell, '0.0##%'));
    put(rr, 3, N(r.hasil, '#,##0.0##', `B${n}*C${n}`));
    put(rr, 4, N(r.exact, '#,##0.0##', `B${n}+D${n}`));
    put(rr, 5, N(r.sell, sellFmt, roundFormula(`E${n}`)));
    if (hasOld) {
      if (r.old !== null && r.old !== undefined) {
        put(rr, 6, N(r.old, moneyFmt(r.old)));
        put(rr, 7, N(r.diff, '#,##0.0##;-#,##0.0##;0', `F${n}-G${n}`));
      }
    }
    const note = [r.name, r.status === 'warn' ? r.message : '']
      .filter(Boolean)
      .join(' | ');
    if (note) put(rr, noteCol, S(note));
  });

  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: rows.length, c: noteCol },
  });
  ws['!cols'] = headers.map((h, c) => {
    if (c === 0) return { wch: 14 };
    if (h === 'HARGA SEBELUM PEMBULATAN') return { wch: 26 };
    if (h === 'KETERANGAN') return { wch: 40 };
    return { wch: 16 };
  });
  ws['!autofilter'] = { ref: ws['!ref'] };

  // Sheet 2: template dua kolom siap pakai (KODE, HARGA)
  const tpl = XLSX.utils.aoa_to_sheet([
    ['KODE', 'HARGA'],
    ...rows.map((r) => [r.code, r.sell]),
  ]);
  tpl['!cols'] = [{ wch: 14 }, { wch: 14 }];

  // Sheet 3: catatan pengaturan yang dipakai
  const roundLabel = !(roundStep > 0)
    ? 'Tanpa pembulatan'
    : `Kelipatan ${roundStep} (${
        (REG_ROUND_MODES.find((m) => m[0] === roundMode) || [])[1] ||
        'Terdekat'
      })`;
  const info = XLSX.utils.aoa_to_sheet([
    ['Pengaturan', 'Nilai'],
    ['Produk', cfg.label],
    ['Judul broadcast', title || '-'],
    [
      'Persentase',
      `${formatPct(pctSigned)} (${pctSigned < 0 ? 'turun' : 'naik'})`,
    ],
    ['Rumus HASIL', 'HARGA DASAR x PERSEN'],
    ['Rumus HARGA JUAL', 'HARGA DASAR + HASIL, lalu dibulatkan'],
    ['Pembulatan', roundLabel],
    ['Sumber harga dasar', meta.baseSource],
    ['File sumber', fileName || '-'],
    ['Jumlah produk', rows.length],
    ['Dibuat', new Date().toLocaleString('id-ID')],
  ]);
  info['!cols'] = [{ wch: 22 }, { wch: 52 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Hasil Harga');
  XLSX.utils.book_append_sheet(wb, tpl, 'Template Update');
  XLSX.utils.book_append_sheet(wb, info, 'Info');
  return wb;
};

const loadRegSettings = () => {
  const base = {
    pcts: {},
    titles: {},
    roundStep: 1,
    roundMode: 'nearest',
    footers: {},
    codeBlock: true,
    sort: true,
  };
  const OLD_TITLES = {
    HTT: 'PULSA THREE REGULER',
    HI: 'PULSA INDOSAT REGULER',
  };
  REG_ORDER.forEach((id) => {
    base.pcts[id] = {
      dir: REG_CONFIG[id].defaultDir,
      value: REG_CONFIG[id].defaultPercent,
    };
    base.titles[id] = REG_CONFIG[id].defaultTitle;
    base.footers[id] = REG_CONFIG[id].defaultFooter;
  });
  try {
    const saved = JSON.parse(localStorage.getItem(REG_SETTINGS_KEY) || 'null');
    if (saved && typeof saved === 'object') {
      REG_ORDER.forEach((id) => {
        const p = saved.pcts && saved.pcts[id];
        if (
          p &&
          (p.dir === 'up' || p.dir === 'down') &&
          typeof p.value === 'string'
        ) {
          base.pcts[id] = { dir: p.dir, value: p.value };
        }
        const t = saved.titles && saved.titles[id];
        if (typeof t === 'string') {
          base.titles[id] = t === OLD_TITLES[id] ? REG_CONFIG[id].defaultTitle : t;
        }
        const f = saved.footers && saved.footers[id];
        if (typeof f === 'string') base.footers[id] = f;
      });
      if (REG_ROUND_STEPS.some((s) => s[0] === saved.roundStep)) {
        base.roundStep = saved.roundStep;
      }
      if (REG_ROUND_MODES.some((m) => m[0] === saved.roundMode)) {
        base.roundMode = saved.roundMode;
      }
      if (typeof saved.codeBlock === 'boolean') base.codeBlock = saved.codeBlock;
      if (typeof saved.sort === 'boolean') base.sort = saved.sort;
    }
  } catch (e) {
    // abaikan, pakai bawaan
  }
  return base;
};

// ----------------------------------------------------------
// KOMPONEN UPDATE HARGA REGULER
// ----------------------------------------------------------
const RegulerPriceUpdate = () => {
  const initial = useMemo(loadRegSettings, []);

  const [opId, setOpId] = useState('HTT');
  const sheets = useMemo(
    () => ({
      HTT: makeRegBuiltinSheet('HTT'),
      HI: makeRegBuiltinSheet('HI'),
    }),
    []
  );
  const [pcts, setPcts] = useState(initial.pcts);
  const [titles, setTitles] = useState(initial.titles);
  const [footers, setFooters] = useState(initial.footers);
  const [roundStep, setRoundStep] = useState(initial.roundStep);
  const [roundMode, setRoundMode] = useState(initial.roundMode);
  const [codeBlock, setCodeBlock] = useState(initial.codeBlock);
  const [sortByPrice, setSortByPrice] = useState(initial.sort);
  const [caption, setCaption] = useState('NONE');

  const [fileError, setFileError] = useState('');

  const [view, setView] = useState('table'); // 'table' | 'telegram'
  const [filter, setFilter] = useState('all'); // 'all' | 'ok' | 'error'
  const [copied, setCopied] = useState(false);
  const [didCopy, setDidCopy] = useState(false);
  const [didDownload, setDidDownload] = useState(false);
  const [toast, setToast] = useState('');

  const previewRef = useRef(null);

  const cfg = REG_CONFIG[opId];
  const sheet = sheets[opId];
  const pct = pcts[opId];

  // Simpan pengaturan
  useEffect(() => {
    try {
      localStorage.setItem(
        REG_SETTINGS_KEY,
        JSON.stringify({
          pcts,
          titles,
          footers,
          roundStep,
          roundMode,
          codeBlock,
          sort: sortByPrice,
        })
      );
    } catch (e) {
      // abaikan jika storage tidak tersedia
    }
  }, [pcts, titles, footers, roundStep, roundMode, codeBlock, sortByPrice]);

  // Notifikasi hilang otomatis
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2200);
    return () => clearTimeout(t);
  }, [copied]);

  // ----- Persen -----
  const pctParsed = useMemo(
    () => parseRegPercent(pct.value, pct.dir),
    [pct.value, pct.dir]
  );
  const pctValid = !pctParsed.error;
  const pctSigned = !pctValid
    ? null
    : pctParsed.value === 0
    ? 0
    : pct.dir === 'down'
    ? -pctParsed.value
    : pctParsed.value;

  // ----- Validasi + perhitungan -----
  const analysis = useMemo(() => {
    if (!sheet) return null;

    const prepared = sheet.parsed.rows.map((r) => ({
      ...r,
      code: normCode(r.code),
    }));

    const counts = {};
    prepared.forEach((r) => {
      if (r.code) counts[r.code] = (counts[r.code] || 0) + 1;
    });

    const rows = [];

    prepared.forEach((r) => {
      if (!r.code && isBlank(r.baseRaw) && !r.dnm) return; // baris kosong

      const row = {
        line: r.line,
        code: r.code,
        name: r.name,
        base: null,
        old: null,
        hasil: null,
        exact: null,
        sell: null,
        diff: null,
        status: 'ok',
        type: null,
        message: '',
      };

      const fail = (type, message) => {
        if (row.status !== 'error') {
          row.status = 'error';
          row.type = type;
          row.message = message;
        }
      };
      const warn = (message) => {
        if (row.status !== 'error') {
          row.status = 'warn';
          row.message = row.message ? `${row.message}; ${message}` : message;
        }
      };

      const baseRes = r.lookupError
        ? { error: 'dnm_tidak_ditemukan', message: r.lookupError }
        : parsePrice(r.baseRaw);
      if (!baseRes.error) row.base = baseRes.value;

      if (!isBlank(r.oldRaw)) {
        const o = parsePrice(r.oldRaw);
        if (!o.error) row.old = o.value;
      }

      if (!r.code) {
        fail('kode_kosong', 'Kode produk kosong');
      } else if (counts[r.code] > 1) {
        fail('duplikat', `Kode muncul ${counts[r.code]}x di Excel`);
      } else {
        const owner = regOperatorOf(r.code);
        if (owner && owner !== opId) {
          fail('operator_salah', `Kode milik ${REG_CONFIG[owner].label}`);
        } else if (!cfg.codePattern.test(r.code)) {
          if (cfg.typoPattern && cfg.typoPattern.test(r.code)) {
            warn(
              `Kode ${r.code} tidak diawali ${cfg.prefix}, kemungkinan salah ketik`
            );
          } else if (cfg.prefixPattern.test(r.code)) {
            warn('Format kode tidak biasa');
          } else {
            fail('kode_tidak_dikenal', `Kode tidak diawali ${cfg.prefix}`);
          }
        }
      }

      if (
        row.status !== 'error' &&
        !isBlank(r.activeRaw) &&
        regIsInactive(r.activeRaw)
      ) {
        fail('nonaktif', 'Produk tidak aktif di Excel');
      }

      if (baseRes.error) fail(baseRes.error, baseRes.message);

      if (row.status !== 'error' && pctSigned !== null) {
        const c = calcReg(row.base, pctSigned, roundStep, roundMode);
        if (!(c.sell > 0)) {
          fail('harga_invalid', 'Harga jual 0 atau kurang');
        } else {
          row.hasil = c.hasil;
          row.exact = c.exact;
          row.sell = c.sell;
          if (row.old !== null) row.diff = c.sell - row.old;
        }
      }

      rows.push(row);
    });

    const validRows = rows.filter((r) => r.status !== 'error');
    const errorRows = rows.filter((r) => r.status === 'error');
    const warnRows = rows.filter((r) => r.status === 'warn');

    const groups = {};
    errorRows.forEach((r) => {
      if (!groups[r.type]) groups[r.type] = [];
      const label = r.code || `Baris ${r.line}`;
      if (!groups[r.type].includes(label)) groups[r.type].push(label);
    });

    return { rows, validRows, errorRows, warnRows, groups };
  }, [sheet, opId, cfg, pctSigned, roundStep, roundMode]);

  const validRows = analysis ? analysis.validRows : [];
  const errorCount = analysis ? analysis.errorRows.length : 0;
  const warnRows = analysis ? analysis.warnRows : [];
  const meta = sheet ? sheet.parsed.meta : null;

  // Baris valid dalam urutan broadcast & Excel hasil
  const orderedValid = useMemo(
    () => (sortByPrice ? [...validRows].sort(regCompare) : validRows),
    [validRows, sortByPrice]
  );

  const canExport = orderedValid.length > 0 && pctSigned !== null;

  const telegram = useMemo(
    () =>
      !canExport
        ? { text: '', width: 0 }
        : buildRegTelegramText({
            title: (titles[opId] || '').trim(),
            rows: orderedValid,
            codeBlock,
            caption,
            footer: footers[opId],
            listGap: cfg.listGap,
          }),
    [canExport, titles, footers, opId, cfg, orderedValid, codeBlock, caption]
  );
  const telegramText = telegram.text;
  const maxLineWidth = telegram.width;

  const visibleRows = useMemo(() => {
    if (!analysis) return [];
    let list = analysis.rows;
    if (filter === 'ok') list = analysis.validRows;
    else if (filter === 'error') list = analysis.errorRows;
    return sortByPrice ? [...list].sort(regCompare) : list;
  }, [analysis, filter, sortByPrice]);

  // Contoh hitungan langsung di kotak persen
  const example = useMemo(() => {
    if (pctSigned === null) return null;
    const firstOk = validRows.find((r) => r.base !== null);
    const base = firstOk ? firstOk.base : 10000;
    return { base, ...calcReg(base, pctSigned, roundStep, roundMode) };
  }, [pctSigned, validRows, roundStep, roundMode]);

  const currentStep = !pctValid
    ? 0
    : didCopy || didDownload
    ? 3
    : view === 'telegram'
    ? 2
    : 1;

  // ----- Handler -----
  const setPctDir = (dir) =>
    setPcts((prev) => ({ ...prev, [opId]: { ...prev[opId], dir } }));

  const handlePctChange = (e) => {
    let v = e.target.value;
    let dir = pcts[opId].dir;
    // mengetik "-" atau "+" di depan angka ikut mengganti arah
    if (/^\s*-/.test(v)) {
      dir = 'down';
      v = v.replace(/^\s*-\s*/, '');
    } else if (/^\s*\+/.test(v)) {
      dir = 'up';
      v = v.replace(/^\s*\+\s*/, '');
    }
    setPcts((prev) => ({ ...prev, [opId]: { dir, value: v } }));
  };

  const setPctChip = (value) =>
    setPcts((prev) => ({ ...prev, [opId]: { ...prev[opId], value } }));

  const handleOperator = (id) => {
    setOpId(id);
    setFilter('all');
    setFileError('');
    setCopied(false);
  };

  const resetProgress = () => {
    setCopied(false);
    setDidCopy(false);
    setDidDownload(false);
  };

  const scrollToPreview = () => {
    if (previewRef.current) {
      previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCopy = async () => {
    if (!telegramText) return;
    const ok = await copyToClipboard(telegramText);
    if (ok) {
      setCopied(true);
      setDidCopy(true);
      setToast('Broadcast berhasil disalin. Tinggal tempel ke Telegram.');
    } else {
      setFileError('Gagal menyalin. Blok teks di preview lalu salin manual.');
    }
  };

  const handleDownload = () => {
    if (!canExport || !sheet) return;
    try {
      const wb = buildRegWorkbook({
        cfg,
        rows: orderedValid,
        pctSigned,
        roundStep,
        roundMode,
        meta: sheet.parsed.meta,
        fileName: sheet.fileName,
        title: (titles[opId] || '').trim(),
      });
      XLSX.writeFile(
        wb,
        `Hasil_Update_Harga_${cfg.label.replace(/\s+/g, '_')}.xlsx`
      );
      setDidDownload(true);
      setToast('Excel hasil berhasil diunduh');
    } catch (err) {
      setFileError(
        `Gagal membuat file Excel: ${err && err.message ? err.message : err}`
      );
    }
  };

  const handleReset = () => {
    setPcts((prev) => ({
      ...prev,
      [opId]: { dir: cfg.defaultDir, value: cfg.defaultPercent },
    }));
    setFileError('');
    setView('table');
    setFilter('all');
    resetProgress();
  };

  // ----- Render -----
  const steps = ['Atur Persen', 'Preview Hasil', 'Preview Telegram', 'Unduh / Salin'];

  const btnBase =
    'text-xs font-bold px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 shadow-sm active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100';

  const inputBase =
    'w-full bg-slate-50 border border-slate-300/80 rounded-xl px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all shadow-sm';

  const isDown = pct.dir === 'down';

  const tableCols = [
    ['Kode', 'text-left'],
    ['Harga Dasar', 'text-right'],
    ['Persen', 'text-right'],
    ['Hasil', 'text-right'],
    ['Harga Jual', 'text-right'],
    ...(meta && meta.hasOld
      ? [
          ['Harga Lama', 'text-right'],
          ['Selisih', 'text-right'],
        ]
      : []),
    ['Status', 'text-left'],
  ];

  const signColor = (n) =>
    n === null || n === undefined || Math.abs(n) < 1e-9
      ? 'text-slate-500'
      : n > 0
      ? 'text-rose-600'
      : 'text-emerald-600';

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6 font-sans relative text-slate-800">
      <style>{`
        @keyframes rpu-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes rpu-pop { 0% { transform: scale(.6); opacity: 0; } 70% { transform: scale(1.08); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
        .rpu-fade-up { animation: rpu-fade-up .35s ease both; }
        .rpu-pop { animation: rpu-pop .4s ease both; }
        @media (prefers-reduced-motion: reduce) {
          .rpu-fade-up, .rpu-pop { animation: none !important; }
        }
      `}</style>

      {/* Notifikasi */}
      <div aria-live="polite" className="pointer-events-none">
        {toast && (
          <div className="rpu-pop fixed bottom-6 right-6 bg-emerald-600 text-white font-semibold text-xs px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2.5 z-50 border border-emerald-500">
            <Icon d={ICONS.check} sw={2.5} />
            <span>{toast}</span>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-md overflow-hidden">
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-slate-50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="space-y-1">
            <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2.5">
              <span className="p-2 bg-indigo-50 rounded-lg text-indigo-600 border border-indigo-100/60 shadow-sm">
                <Icon d={ICONS.table} className="w-5 h-5" />
              </span>
              Update Harga Reguler (Three &amp; Indosat)
            </h2>
            <p className="text-xs text-slate-500">
              Data produk sudah tersedia. Tinggal atur persen naik atau turun,
              lihat hasilnya, lalu salin broadcast Telegram atau unduh Excel
              hasil.
            </p>
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-6">
          {/* Indikator alur */}
          <ol className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {steps.map((label, i) => {
              const done = i < currentStep;
              const active = i === currentStep;
              return (
                <li
                  key={label}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors duration-300 ${
                    active
                      ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                      : done
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-slate-50 text-slate-400'
                  }`}
                >
                  <span
                    className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      active
                        ? 'bg-indigo-600 text-white'
                        : done
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {done ? (
                      <Icon d={ICONS.check} className="w-3 h-3" sw={3} />
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span className="truncate">{label}</span>
                </li>
              );
            })}
          </ol>

          {/* Pilih produk */}
          <div className="flex flex-wrap gap-2">
            {REG_ORDER.map((id) => (
              <button
                key={id}
                onClick={() => handleOperator(id)}
                className={`text-xs font-bold px-4 py-2 rounded-xl border transition-all duration-200 active:scale-95 flex items-center gap-2 ${
                  opId === id
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                    : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                }`}
              >
                <span>{REG_CONFIG[id].label}</span>
                <span
                  className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${
                    opId === id
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {REG_CONFIG[id].prefix}
                </span>
              </button>
            ))}
          </div>

          {/* Pengaturan persen + pembulatan */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 rounded-xl border border-indigo-100 bg-indigo-50/50 p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label
                  htmlFor="rpu-pct"
                  className="text-xs font-bold text-slate-700"
                >
                  Persentase {cfg.label}
                </label>
                <div
                  role="group"
                  aria-label="Arah persentase"
                  className="inline-flex gap-1 p-1 bg-white rounded-lg border border-indigo-100"
                >
                  <button
                    type="button"
                    aria-pressed={!isDown}
                    onClick={() => setPctDir('up')}
                    className={`text-[11px] font-bold px-3 py-1.5 rounded-md transition-all ${
                      !isDown
                        ? 'bg-rose-600 text-white shadow-sm'
                        : 'text-slate-600 hover:text-rose-600'
                    }`}
                  >
                    ▲ Naik
                  </button>
                  <button
                    type="button"
                    aria-pressed={isDown}
                    onClick={() => setPctDir('down')}
                    className={`text-[11px] font-bold px-3 py-1.5 rounded-md transition-all ${
                      isDown
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'text-slate-600 hover:text-emerald-600'
                    }`}
                  >
                    ▼ Turun
                  </button>
                </div>
              </div>

              <div className="relative">
                <input
                  id="rpu-pct"
                  type="text"
                  inputMode="decimal"
                  value={pct.value}
                  onChange={handlePctChange}
                  placeholder="Contoh: 0,1"
                  className={`${inputBase} pr-9 ${
                    pctValid
                      ? ''
                      : '!border-red-400 focus:!border-red-500 focus:!ring-red-500/20'
                  }`}
                />
                <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none">
                  %
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {REG_PERCENT_CHIPS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setPctChip(c)}
                    className={`text-[11px] font-bold px-2.5 py-1 rounded-lg border transition-all ${
                      pct.value === c
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {c}%
                  </button>
                ))}
              </div>

              {!pctValid ? (
                <p className="text-[11px] text-red-600 font-semibold">
                  {pctParsed.error}
                </p>
              ) : (
                <div className="text-[11px] text-slate-600 space-y-0.5">
                  <p>
                    Hasil = harga dasar x{' '}
                    <span
                      className={`font-bold ${
                        isDown ? 'text-emerald-600' : 'text-rose-600'
                      }`}
                    >
                      {formatPct(pctSigned)}
                    </span>{' '}
                    ({isDown ? 'turun' : 'naik'}). Harga jual = harga dasar +
                    hasil.
                  </p>
                  {example && (
                    <p>
                      Contoh: {formatRupiah(example.base)} →{' '}
                      <span className="font-bold text-slate-800">
                        {formatSigned(example.hasil)}
                      </span>{' '}
                      → harga jual{' '}
                      <span className="font-bold text-slate-800">
                        {formatDec(example.sell)}
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="rpu-step"
                  className="text-xs font-bold text-slate-700"
                >
                  Pembulatan Harga Jual
                </label>
                <select
                  id="rpu-step"
                  value={roundStep}
                  onChange={(e) => setRoundStep(Number(e.target.value))}
                  className={`${inputBase} cursor-pointer`}
                >
                  {REG_ROUND_STEPS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="rpu-mode"
                  className="text-xs font-bold text-slate-700"
                >
                  Arah Pembulatan
                </label>
                <select
                  id="rpu-mode"
                  value={roundMode}
                  disabled={!(roundStep > 0)}
                  onChange={(e) => setRoundMode(e.target.value)}
                  className={`${inputBase} cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {REG_ROUND_MODES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Pengaturan broadcast */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="rpu-title"
                className="text-xs font-bold text-slate-700"
              >
                Judul Broadcast
              </label>
              <input
                id="rpu-title"
                type="text"
                value={titles[opId]}
                onChange={(e) =>
                  setTitles((prev) => ({ ...prev, [opId]: e.target.value }))
                }
                placeholder={cfg.defaultTitle}
                className={inputBase}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-slate-700">Caption</span>
              <div className="inline-flex gap-1 p-1 bg-slate-100 rounded-xl border border-slate-200/80 w-full">
                {TPL_CAPTIONS.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCaption(c.id)}
                    className={`flex-1 text-[11px] font-bold px-2 py-1.5 rounded-lg transition-all ${
                      caption === c.id
                        ? c.on
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2 justify-end">
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sortByPrice}
                  onChange={(e) => setSortByPrice(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                Urutkan dari harga termurah ke termahal
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={codeBlock}
                  onChange={(e) => setCodeBlock(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                Bungkus code block Telegram (```)
              </label>
            </div>
          </div>

          {/* Footer broadcast */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <label
                htmlFor="rpu-footer"
                className="text-xs font-bold text-slate-700"
              >
                Footer Broadcast {cfg.label}{' '}
                <span className="font-normal text-slate-400">
                  (info produk &amp; link, tampil di bawah list harga)
                </span>
              </label>
              <button
                type="button"
                onClick={() =>
                  setFooters((prev) => ({
                    ...prev,
                    [opId]: cfg.defaultFooter,
                  }))
                }
                className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800"
              >
                Kembalikan bawaan
              </button>
            </div>
            <textarea
              id="rpu-footer"
              rows={9}
              value={footers[opId]}
              onChange={(e) =>
                setFooters((prev) => ({ ...prev, [opId]: e.target.value }))
              }
              spellCheck={false}
              className={`${inputBase} font-mono !font-normal leading-relaxed resize-y`}
            />
          </div>

          {/* Info data bawaan */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-bold text-slate-800">
              Data {cfg.label}: {sheet.parsed.rows.length} produk
            </span>
            <span className="text-slate-400">•</span>
            <span>Harga dasar dari {sheet.parsed.meta.baseSource}</span>
          </div>

          {fileError && (
            <div className="rpu-fade-up rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 flex items-start gap-2.5">
              <Icon d={ICONS.alert} className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{fileError}</span>
            </div>
          )}

          {/* HASIL SETELAH UPLOAD */}
          {analysis && (
            <div ref={previewRef} className="rpu-fade-up space-y-5 scroll-mt-4">
              {/* Statistik */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  {
                    k: 'Total Baris',
                    v: analysis.rows.length,
                    c: 'text-slate-900',
                  },
                  { k: 'Valid', v: validRows.length, c: 'text-emerald-600' },
                  {
                    k: 'Bermasalah',
                    v: errorCount,
                    c: errorCount ? 'text-red-600' : 'text-slate-400',
                  },
                  {
                    k: isDown ? 'Persen (Turun)' : 'Persen (Naik)',
                    v: pctSigned === null ? '-' : formatPct(pctSigned),
                    c: isDown ? 'text-emerald-600' : 'text-rose-600',
                  },
                ].map((s) => (
                  <div
                    key={s.k}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
                  >
                    <p className="text-[11px] font-semibold text-slate-500">
                      {s.k}
                    </p>
                    <p className={`text-xl font-bold mt-0.5 ${s.c}`}>{s.v}</p>
                  </div>
                ))}
              </div>

              <p className="text-[11px] text-slate-500">
                Harga dasar bawaan:{' '}
                <span className="font-semibold text-slate-700">
                  {meta.baseSource}
                </span>
                .
              </p>

              {/* Validasi */}
              {errorCount > 0 ? (
                <div
                  className={`rpu-fade-up rounded-xl border p-4 space-y-3 ${
                    validRows.length === 0
                      ? 'border-red-200 bg-red-50'
                      : 'border-amber-200 bg-amber-50'
                  }`}
                >
                  <p
                    className={`text-xs font-bold flex items-center gap-2 ${
                      validRows.length === 0 ? 'text-red-700' : 'text-amber-800'
                    }`}
                  >
                    <Icon d={ICONS.alert} />
                    {errorCount} BARIS DILEWATI (tidak ikut broadcast &amp; Excel
                    hasil)
                  </p>
                  <div
                    className={`space-y-2 text-xs ${
                      validRows.length === 0 ? 'text-red-800' : 'text-amber-900'
                    }`}
                  >
                    {Object.keys(analysis.groups).map((type) => (
                      <div key={type}>
                        <p className="font-semibold">
                          {analysis.groups[type].length}{' '}
                          {REG_ERROR_LABELS[type] || type}:
                        </p>
                        <p className="font-mono text-[11px] break-words">
                          {analysis.groups[type].join(', ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rpu-fade-up rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-700 flex items-center gap-2">
                  <Icon d={ICONS.check} sw={2.5} />
                  Validasi berhasil. Semua {validRows.length} produk valid.
                </div>
              )}

              {warnRows.length > 0 && (
                <div className="rpu-fade-up rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-xs text-amber-900 space-y-1">
                  <p className="font-bold">
                    {warnRows.length} peringatan (tetap diproses, mohon dicek):
                  </p>
                  {warnRows.slice(0, 8).map((r) => (
                    <p key={`${r.line}-${r.code}`} className="text-[11px]">
                      <span className="font-mono font-bold">{r.code}</span>
                      {' - '}
                      {r.message}
                    </p>
                  ))}
                  {warnRows.length > 8 && (
                    <p className="text-[11px]">
                      ...dan {warnRows.length - 8} lainnya.
                    </p>
                  )}
                </div>
              )}

              {/* Tombol aksi */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setView('table');
                    scrollToPreview();
                  }}
                  className={`${btnBase} ${
                    view === 'table'
                      ? 'bg-slate-900 text-white'
                      : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Icon d={ICONS.table} />
                  <span>Preview Hasil</span>
                </button>
                <button
                  onClick={() => {
                    setView('telegram');
                    scrollToPreview();
                  }}
                  className={`${btnBase} ${
                    view === 'telegram'
                      ? 'bg-slate-900 text-white'
                      : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Icon d={ICONS.send} />
                  <span>Preview Telegram</span>
                </button>
                <button
                  onClick={handleCopy}
                  disabled={!telegramText}
                  className={`${btnBase} ${
                    copied
                      ? 'bg-emerald-600 text-white'
                      : 'bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                  }`}
                >
                  <Icon d={copied ? ICONS.check : ICONS.copy} />
                  <span>{copied ? 'Tersalin' : 'Salin Broadcast'}</span>
                </button>
                <button
                  onClick={handleDownload}
                  disabled={!canExport}
                  className={`${btnBase} bg-indigo-600 hover:bg-indigo-700 text-white`}
                >
                  <Icon d={ICONS.download} />
                  <span>Unduh Excel</span>
                </button>
                <button
                  onClick={handleReset}
                  className={`${btnBase} bg-white border border-slate-300 text-slate-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200`}
                >
                  <Icon d={ICONS.x} />
                  <span>Reset Persen</span>
                </button>
              </div>

              {!pctValid && (
                <p className="text-xs text-red-600 font-semibold">
                  Persentase tidak valid, harga jual belum bisa dihitung.
                </p>
              )}

              {/* PREVIEW TABEL */}
              {view === 'table' && (
                <div key="table" className="rpu-fade-up space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-900">
                      Hasil Update Harga {cfg.label}
                    </h3>
                    <div className="flex gap-1.5">
                      {[
                        ['all', 'Semua'],
                        ['ok', 'Valid'],
                        ['error', 'Bermasalah'],
                      ].map(([k, l]) => (
                        <button
                          key={k}
                          onClick={() => setFilter(k)}
                          className={`text-[11px] font-bold px-3 py-1.5 rounded-lg border transition-all ${
                            filter === k
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 overflow-auto max-h-[460px] shadow-sm">
                    <table className="w-full min-w-[680px] text-xs border-collapse">
                      <thead>
                        <tr className="text-slate-600">
                          {tableCols.map(([h, a]) => (
                            <th
                              key={h}
                              className={`sticky top-0 z-10 bg-slate-50 border-b border-slate-200 px-4 py-3 font-bold ${a}`}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody key={filter}>
                        {visibleRows.length === 0 && (
                          <tr>
                            <td
                              colSpan={tableCols.length}
                              className="px-4 py-8 text-center text-slate-400"
                            >
                              Tidak ada data untuk ditampilkan.
                            </td>
                          </tr>
                        )}
                        {visibleRows.map((r) => (
                          <tr
                            key={`${r.line}-${r.code}`}
                            className={`rpu-fade-up border-b border-slate-100 transition-colors duration-200 ${
                              r.status === 'error'
                                ? 'bg-red-50/60 hover:bg-red-50'
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            <td className="px-4 py-2.5 font-mono font-bold text-slate-800">
                              {r.code || '-'}
                              {r.name && (
                                <span className="block text-[10px] font-normal text-slate-400">
                                  {r.name}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-600">
                              {r.base !== null ? formatDec(r.base) : '-'}
                            </td>
                            <td
                              className={`px-4 py-2.5 text-right font-mono tabular-nums ${signColor(
                                pctSigned
                              )}`}
                            >
                              {r.sell !== null ? formatPct(pctSigned) : '-'}
                            </td>
                            <td
                              className={`px-4 py-2.5 text-right font-mono tabular-nums ${signColor(
                                r.hasil
                              )}`}
                            >
                              {r.hasil !== null ? formatSigned(r.hasil) : '-'}
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono tabular-nums font-bold text-slate-900">
                              {r.sell !== null ? formatDec(r.sell) : '-'}
                            </td>
                            {meta.hasOld && (
                              <>
                                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-500">
                                  {r.old !== null ? formatDec(r.old) : '-'}
                                </td>
                                <td
                                  className={`px-4 py-2.5 text-right font-mono tabular-nums ${signColor(
                                    r.diff
                                  )}`}
                                >
                                  {r.diff !== null ? formatSigned(r.diff) : '-'}
                                </td>
                              </>
                            )}
                            <td className="px-4 py-2.5">
                              {r.status === 'ok' && (
                                <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 text-[11px] font-semibold">
                                  Valid
                                </span>
                              )}
                              {r.status === 'warn' && (
                                <span
                                  title={r.message}
                                  className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 text-[11px] font-semibold"
                                >
                                  {r.message}
                                </span>
                              )}
                              {r.status === 'error' && (
                                <span
                                  title={r.message}
                                  className="inline-flex items-center rounded-full bg-red-50 text-red-700 border border-red-200 px-2.5 py-0.5 text-[11px] font-semibold"
                                >
                                  {r.message}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Harga Dasar = data bawaan. Hasil = Harga Dasar x Persen
                    (hijau = turun, merah = naik). Harga Jual = Harga Dasar +
                    Hasil, lalu dibulatkan sesuai pilihan.
                    {meta.hasOld &&
                      ' Selisih = Harga Jual baru dikurangi Harga Lama (Hrg. Jual lama).'}
                  </p>
                </div>
              )}

              {/* PREVIEW TELEGRAM */}
              {view === 'telegram' && (
                <div key="telegram" className="rpu-fade-up space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-900">
                      Preview Broadcast Telegram
                    </h3>
                    <span className="text-[11px] text-slate-500">
                      Lebar list harga: {maxLineWidth} karakter
                      {maxLineWidth > 38 &&
                        ' (bisa terpotong di layar HP kecil)'}
                    </span>
                  </div>

                  <div className="rounded-xl bg-slate-900 border border-slate-800 shadow-inner p-4 overflow-auto max-h-[460px]">
                    {telegramText ? (
                      <pre className="text-emerald-400 font-mono text-xs leading-relaxed whitespace-pre select-all selection:bg-emerald-900/60 selection:text-emerald-200">
                        {telegramText}
                      </pre>
                    ) : (
                      <p className="text-slate-500 italic text-xs">
                        Belum ada produk valid untuk dibroadcast.
                      </p>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500">
                    List harga memakai spasi monospace dan dibungkus code
                    block, jadi kolom harga tetap sejajar di desktop, web, dan
                    mobile. Footer ada di luar code block supaya link bisa
                    diklik.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==========================================================
// KOMPONEN UTAMA: TELEGRAM (tab Template Harga Digiflazz + tab Update Harga Game + tab Update Harga Reguler)
// ==========================================================
const TelegramUpload = ({ products, onApply }) => {
  // ==========================================
  // STATE
  // ==========================================
  const [activeTab, setActiveTab] = useState('telegram');

  // Pengaturan tampilan
  const [categoryName, setCategoryName] = useState('');
  const [prefixCaption, setPrefixCaption] = useState('NONE');
  const [sortByPrice, setSortByPrice] = useState(true);

  // Sumber data
  const [inputText, setInputText] = useState('');
  const [sourceRows, setSourceRows] = useState([]); // [{ line, code, price }]
  const [sourceLabel, setSourceLabel] = useState('');
  const [genId, setGenId] = useState(0); // naik tiap data baru (memicu animasi baris)
  const [reading, setReading] = useState({ active: false, progress: 0 });
  const [inputError, setInputError] = useState('');

  // Progres panduan & notifikasi
  const [copied, setCopied] = useState(false);
  const [didCopy, setDidCopy] = useState(false);
  const [didDownload, setDidDownload] = useState(false);
  const [toast, setToast] = useState('');

  // Hasil format dihitung otomatis: ubah judul/caption/urutan langsung tampil
  const { text: outputText, items, skipped } = useMemo(
    () =>
      buildTemplateData(sourceRows, {
        title: categoryName,
        prefix: prefixCaption,
        sortByPrice,
      }),
    [sourceRows, categoryName, prefixCaption, sortByPrice]
  );

  const hasData = items.length > 0;

  // Notifikasi & status tombol salin hilang otomatis
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  // ==========================================
  // HANDLER
  // ==========================================
  const loadRows = (rows, label) => {
    setSourceRows(rows);
    setSourceLabel(label);
    setGenId((g) => g + 1);
    setDidCopy(false);
    setDidDownload(false);
    setInputError('');
  };

  const handleFile = async (file) => {
    if (!file) return;
    setInputError('');

    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      setInputError(
        'Format file salah. Gunakan file Excel (.xlsx / .xls) atau .csv.'
      );
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setInputError('Ukuran file terlalu besar (maksimal 5 MB).');
      return;
    }

    setReading({ active: true, progress: 5 });
    try {
      const rows = await readSheetFile(file, (p) =>
        setReading({ active: true, progress: p })
      );
      setReading({ active: true, progress: 100 });
      await delay(300);
      loadRows(rows, file.name);
    } catch (err) {
      setInputError(err.message || 'File tidak dapat diproses.');
    } finally {
      setReading({ active: false, progress: 0 });
    }
  };

  const handleGenerateText = () => {
    if (!inputText.trim()) {
      setInputError('Isi dulu datanya. Format: KODE HARGA, satu produk per baris.');
      return;
    }
    const rows = inputText.split('\n').map((line, i) => {
      const parts = line.trim().split(/\s+/);
      return { line: i + 1, code: parts[0], price: parts.slice(1).join('') };
    });
    loadRows(rows, 'Teks manual');
  };

  const handleLoadSample = () => {
    setInputText(TPL_SAMPLE_TEXT);
    setInputError('');
  };

  const handleReset = () => {
    setInputText('');
    setSourceRows([]);
    setSourceLabel('');
    setInputError('');
    setCopied(false);
    setDidCopy(false);
    setDidDownload(false);
  };

  const handleCopy = async () => {
    if (!outputText) return;
    const ok = await copyToClipboard(outputText);
    if (ok) {
      setCopied(true);
      setDidCopy(true);
      setToast('Berhasil disalin ke clipboard');
    } else {
      setInputError('Gagal menyalin. Blok teks di preview lalu salin manual.');
    }
  };

  // Unduh template Excel untuk update harga di Digiflazz
  const handleDownloadDigiflazzTemplate = () => {
    if (!items.length) return;

    const data = [
      ['Buyer SKU Code', 'Harga Baru / Margin'],
      ...items.map((item) => [item.sku, item.price]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [{ wch: 25 }, { wch: 20 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Update Harga Digiflazz');

    const fileName = `Template_Update_Harga_Digiflazz_${
      categoryName.replace(/\s+/g, '_') || 'Produk'
    }.xlsx`;
    XLSX.writeFile(wb, fileName);

    setDidDownload(true);
    setToast('Template Digiflazz berhasil diunduh');
  };

  // ==========================================
  // RENDER
  // ==========================================
  const tabClass = (id) =>
    `text-xs font-bold px-4 py-2 rounded-lg transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
      activeTab === id
        ? 'bg-white text-slate-900 shadow-sm'
        : 'text-slate-500 hover:text-slate-700'
    }`;

  return (
    <>
      {/* Pengalih tab */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6 font-sans">
        <div
          role="tablist"
          className="inline-flex gap-1 p-1 bg-slate-100 rounded-xl border border-slate-200/80"
        >
          <button
            role="tab"
            aria-selected={activeTab === 'telegram'}
            onClick={() => setActiveTab('telegram')}
            className={tabClass('telegram')}
          >
            Template Harga Digiflazz
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'game'}
            onClick={() => setActiveTab('game')}
            className={tabClass('game')}
          >
            Update Harga Game
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'reguler'}
            onClick={() => setActiveTab('reguler')}
            className={tabClass('reguler')}
          >
            Update Harga Reguler
          </button>
        </div>
      </div>

      {activeTab === 'game' ? (
        <GamePriceUpdate products={products} onApply={onApply} />
      ) : activeTab === 'reguler' ? (
        <RegulerPriceUpdate />
      ) : (
        <DigiflazzTemplatePanel
          categoryName={categoryName}
          setCategoryName={setCategoryName}
          prefixCaption={prefixCaption}
          setPrefixCaption={setPrefixCaption}
          sortByPrice={sortByPrice}
          setSortByPrice={setSortByPrice}
          inputText={inputText}
          setInputText={setInputText}
          onGenerateText={handleGenerateText}
          onLoadSample={handleLoadSample}
          sourceLabel={sourceLabel}
          reading={reading}
          inputError={inputError}
          onFile={handleFile}
          outputText={outputText}
          items={items}
          skipped={skipped}
          genId={genId}
          copied={copied}
          onCopy={handleCopy}
          onDownload={handleDownloadDigiflazzTemplate}
          onReset={handleReset}
          stepsDone={[hasData, didCopy, didDownload]}
        />
      )}

      {/* Notifikasi */}
      <div aria-live="polite" className="pointer-events-none">
        {toast && (
          <div className="tpl-pop fixed bottom-6 right-6 z-50 flex items-center gap-2.5 rounded-xl border border-emerald-500 bg-emerald-600 px-4 py-3 text-xs font-semibold text-white shadow-2xl">
            <Icon d={ICONS.check} className="w-4 h-4" sw={2.5} />
            <span>{toast}</span>
          </div>
        )}
      </div>
    </>
  );
};

export default TelegramUpload;